import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import IkiSymbol from '@/components/IkiSymbol';
import AudioRecorder from '@/components/AudioRecorder';
import { Headphones, Cycle, Save, Mic, Meditate, Alert, GriefIcon, AnxietyIcon, BurnoutIcon, IntegrationIcon, RedesignIcon, Bloom, Leaf } from '@/components/HypIcons';
import MicroDosingCalendar from '@/pages/MicroDosingCalendar';
import IntegrationStudioSection from '@/pages/IntegrationStudioSection';
import CrisisScreen from '@/components/CrisisScreen';
import useCrisisDetection from '@/hooks/useCrisisDetection';
import PaywallCard from '@/components/PaywallCard';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import ConfirmModal from '@/components/ConfirmModal';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const PROG_META = {
  grief:       { Icon: GriefIcon,       color: '#4A7B9D', label: 'Grief & Loss'        },
  anxiety:     { Icon: AnxietyIcon,     color: '#2D8B87', label: 'Anxiety & Overwhelm' },
  burnout:     { Icon: BurnoutIcon,     color: '#B8906A', label: 'Burnout Recovery'     },
  integration: { Icon: IntegrationIcon, color: '#7B68A0', label: 'Trauma Integration'  },
  redesign:    { Icon: RedesignIcon,    color: '#1E6B68', label: 'Redesign Your Mind'   },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── IndexedDB persistence for voice session blobs ────────────────────────────
// Blobs survive page refreshes and browser crashes — never lose a recording.
const AudioStorage = {
  DB_NAME: 'hypseya_voice_v1',
  STORE: 'blobs',
  _db: null,

  async db() {
    if (this._db) return this._db;
    this._db = await new Promise((resolve, reject) => {
      const req = indexedDB.open(this.DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(this.STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this._db;
  },

  key(userId, sessionId) { return `${userId}_s${sessionId}`; },

  async save(userId, sessionId, blob) {
    try {
      const db = await this.db();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(this.STORE, 'readwrite');
        tx.objectStore(this.STORE).put(blob, this.key(userId, sessionId));
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    } catch (e) { console.warn('AudioStorage.save failed:', e); }
  },

  async load(userId, sessionId) {
    try {
      const db = await this.db();
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(this.STORE, 'readonly');
        const req = tx.objectStore(this.STORE).get(this.key(userId, sessionId));
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
      });
    } catch (e) { return null; }
  },

  async clearUser(userId) {
    try {
      const db = await this.db();
      const tx = db.transaction(this.STORE, 'readwrite');
      const store = tx.objectStore(this.STORE);
      const allKeys = await new Promise((res, rej) => {
        const r = store.getAllKeys(); r.onsuccess = () => res(r.result); r.onerror = rej;
      });
      allKeys.filter(k => k.startsWith(`${userId}_`)).forEach(k => store.delete(k));
    } catch (e) { console.warn('AudioStorage.clearUser failed:', e); }
  },
};

// ============ VOICE SESSION SCRIPTS ============
const VOICE_SESSIONS = [
  {
    id: 1, title: 'Arrival', subtitle: 'Presence & the I Am', duration: '5 min',
    instruction: 'Read at a natural, unhurried pace. Leave a breath between each paragraph — as if you\'re speaking to someone you care about.',
    script: `My voice is not just sound.

It's a signal. A frequency. Something that belongs entirely to me.

Before you start recording, take one breath in... and let it go.

Good.

I want to talk about why we're here. Not why you're here reading these words — but why this specific act, recording your own voice, matters more than it might seem right now.

The human nervous system responds to familiar voices differently than it responds to strangers. There's actual biology behind this. When you hear a voice you trust — really trust — your body does something. It softens. The shoulders drop a little. The jaw unclenches. The heart rate slows, sometimes just fractionally, but measurably.

Whose voice does your nervous system trust most, before any other?

Yours.

Not because you've earned it. Not because you're calm or wise or have it all figured out. But because your body has been listening to your voice since before you were born. It knows your frequency. It knows your rhythm. It knows you.

This is what HYPSEYA is built on.

The voice I'm giving to this app — these recordings, these words — will come back to me at the exact moments I need them most. In the middle of the night. In the middle of anxiety. In the middle of a moment where I've lost the thread back to myself.

And the voice I'll hear in those moments... will be mine.

I Am here.

I Am present.

I Am, in this moment, exactly where I need to be.

Take a slow breath now. In through the nose...

Hold gently...

And out through the mouth.

Feel where you are. The ground beneath you. The air. The particular quality of this moment.

This is not a task. It's something you're giving to a future version of yourself who's going to need it. Speak slowly. Let your voice be warm — not performed, not careful. Just yours.

I Am ready.

I Am, right now, beginning.`
  },
  {
    id: 2, title: 'Narrative', subtitle: 'Who I am beyond my patterns', duration: '10 min',
    instruction: 'Read with warmth and honesty. Let yourself feel what you\'re saying — not as performance, but as something you\'re choosing to make real.',
    script: `There's a story I've been telling about myself for a long time.

Not the events — the interpretation. The voice that takes what happens and decides what it means about who I am.

And here's the thing I'm learning: that voice wasn't mine to begin with.

It was assembled over years. It absorbed the messages of childhood, the conclusions drawn in moments of pain or confusion, the beliefs inherited from people who were themselves trying to make sense of things. By the time I was an adult, this internal narrator had very specific opinions about me. About what I was capable of. About what I deserved. About what was probably going to go wrong.

And for a long time, I just accepted it. Because it was familiar. Because it was loud. Because I didn't know you could question it.

But you can.

The brain isn't fixed. This is one of the most important things neuroscience has shown us in the last twenty years. The brain changes in response to what we repeatedly practise, think, and say. Every thought we repeat becomes a neural pathway. Every story we tell about ourselves gets reinforced every time we tell it.

Which means the story can be rewritten.

Not by pretending the hard chapters didn't happen. Not by covering difficulty with false positivity. But by choosing — deliberately, consciously — a different narration.

I Am someone who has survived things I didn't think I could survive.

I Am someone who has learned from experience — not despite difficulty, but through it. The hardest seasons weren't just obstacles. They were information. They showed me what I was made of.

I Am someone with more resilience than is visible to me in hard moments. I have proven this. There were days I genuinely didn't know how I'd get through. And I got through. Not perfectly. Not without cost. But I got through.

Take a breath.

Out.

I Am in relationship with change. Not always comfortable with it. Not always graceful. But willing. Willing to grow, even when it's uncomfortable. Willing to outgrow versions of myself I was attached to — because the self isn't a fixed point. It's a direction.

I have a body that has been trying to communicate with me for years. And I'm learning to listen. The tension that arrives before a difficult conversation. The heaviness at the end of a week that asked too much. The expansion I feel in real moments of connection. My body is not a problem. It's an intelligence. And it's always been speaking — I'm just finally learning the language.

There are things I know about myself that don't change. My care for the people I love. My capacity to find meaning in small things — the quality of light at a certain time of day, a conversation that unexpectedly shifts something, the strange comfort of being in a place that feels like it's been here much longer than I have.

These are not achievements. They're character. They're who I am beneath the patterns I learned, beneath the adaptations I built, beneath the armour that once served me and that I am now, slowly, choosing to put down.

I believe the mind can be redesigned.

Not all at once. Not without effort. Not without returning to the practice again and again when I forget — and I will forget. But the redesign is possible. I've already felt it. Small shifts in how I respond to things. A breath taken before a reaction. A different sentence offered instead of the familiar one.

These are not small things. They are the architecture of a new life.

I Am learning to trust myself.

I Am someone who shows up — imperfectly, incompletely, but honestly. And honestly is enough.

I Am allowed to change my mind about who I am. I'm allowed to outgrow the self I was — and step, without apology, into the self I'm becoming.

I Am the author of this story. Not in the sense that I control what happens. But in the sense that I choose what it means. I choose how to narrate the chapters already written, and how to orient toward the ones still ahead.

Take a slow breath.

Let what you've just spoken settle. Not just in the mind. In the body. In the quiet intelligence that has been listening all along.

I Am not the worst thing that happened to me.

I Am not the hardest season I lived through.

I Am — right now, in this moment — someone who has decided to take the pen back.`
  },
  {
    id: 3, title: 'Regulation', subtitle: 'The body that heals itself', duration: '10 min',
    instruction: 'This is the most important session. Speak slowly, as if guiding yourself home. Warm, unhurried — let the words have space.',
    script: `I want to talk about the body.

Not as a machine to be optimised. Not as a symptom to be managed. But as an intelligence — ancient, adaptive, extraordinarily responsive — that has been trying to communicate with me for as long as I've been alive.

I haven't always known how to listen.

The nervous system learns from experience. Not just from what happens — but from how we relate to what happens. Over years of living, it develops patterns. It learns which situations require alertness and which are safe. It learns to associate certain sensations with certain meanings. And it acts from those patterns, often faster than conscious thought can intervene.

This isn't a flaw. This is protection.

Every pattern of reactivity, every tendency toward hypervigilance or shutdown, every emotional response that feels bigger than the moment has, at its root, an old learning. A survival strategy that once made sense. The nervous system has never been trying to make things harder. It's been trying to keep me safe — using the best information it had at the time.

HYPSEYA doesn't ask me to override these patterns by force.

It asks something different: to practise safety, until the body learns it from the inside.

Take a breath.

Slow.

There's a nerve called the vagus nerve — it wanders through the body, touching almost every organ. And it is extraordinarily sensitive to the prosody of voice. The tone. The rhythm. The warmth. The pace. The nervous system reads voice constantly, looking for signals that say: you are safe. You can soften. You don't need to be vigilant right now.

When I hear my own voice in a state of calm — slow, unhurried, grounded — I'm sending my nervous system one of the most credible safety signals it can receive. Because this voice isn't foreign. It isn't asking me to trust a stranger. It's asking me to trust myself. And the body, at some fundamental level, already knows this voice.

I Am safe in this body.

Not as a fantasy. As a practice. As a state I'm teaching my nervous system to return to.

Let your shoulders drop.

Let your jaw release.

Let the space behind your eyes soften.

The mind and the body are not separate systems. They are one system in continuous conversation. What the mind believes, the body enacts. What the body feels, the mind interprets. This conversation is happening constantly — and we can enter it deliberately, through the simple act of slowing down and choosing what we bring into it.

This is not about denial. I'm not pretending difficulty doesn't exist. I'm not saying thought alone resolves what is real and physical and complex. The body has its own requirements, its own timing, its own wisdom.

But alongside everything that is hard, there is also a capacity within me — biological, not magical — to return to balance. To regulate. To find the stillness that exists beneath the storm.

The body knows how to heal. It knows how to find equilibrium when given the conditions to do so. My cells are in continuous renewal. My brain is in ongoing dialogue with experience — updating, adjusting, always available to new learning.

I don't need to force healing. I need to create the conditions for it.

And one of those conditions is this: a voice. Calm. Known. Mine.

I Am a self-organising system.

I Am whole, even in the moments I feel fragmented.

I Am healing — even on the days I can't see it.

Bring your attention inward.

Feel the weight of your body. The contact with the ground. The breath moving in and out.

I Am the stillness that exists beneath the storm. It's always there. It doesn't require the storm to stop. It only requires me to remember where to look.

I Am learning to inhabit this body with less fear and more curiosity. Less demand and more listening.

Take a slow breath in.

Long exhale out.

Let the body know it is heard. Let it know you are here. Let it know this voice — your voice — will return to it, again and again, with this same quality of care.

I Am my own medicine.`
  },
  {
    id: 4, title: 'Integration', subtitle: 'The voice that returns', duration: '5 min',
    instruction: 'Speak these words as a commitment. Slowly, deliberately — as if you\'re speaking to the version of yourself who will need this most.',
    script: `We're here.

Four sessions. Four conversations with yourself that you'll carry forward.

I want to stay with that for a moment — because it's more significant than it might feel right now, in the ordinary light of wherever you are reading this.

What you've recorded across these sessions is not just audio. It's a resource. One that belongs to you completely. That no one else can replicate. That will return to you exactly as you gave it — with your frequency, your rhythm, your particular way of breathing between sentences.

That matters more than it may seem today.

There will be days when regulation feels far away. When the morning arrives already heavy. When something happens and the nervous system responds before you have the chance to choose. In those moments, the mind reaches for anchors. For something familiar that says: you've been here before. You found your way through.

Your voice will be that anchor.

Not as a performance of calm. Not as a demand to feel something different. But as a gentle, consistent return to something you already know — a state of regulation that you recorded, that you embodied, that is now available to you whenever you need it.

HYPSEYA's deepest purpose is this: to put the most important guidance in your own voice. Not because you have all the answers. But because you are the one who most needs to trust the person speaking.

I Am my own guide, in the moments when I forget who I am.

I Am someone who practises — not someone who performs.

I Am willing to return — again and again — to myself. Even after long absences. Even after difficult seasons. The return is always available.

Take a final long breath.

In through the nose.

Out through the mouth.

Feel the weight of your body. The ground beneath you. The air. This moment.

This body that holds you. This voice that will guide you. This practice that asks only that you return.

I Am ready.

I Am, right now, enough.

And the voice that will find me in my hardest moments — the one that knows my frequency, that speaks in the exact rhythm of my own interior — that voice is here.

It has always been here.

It is mine.`
  },
];

// ============ VOICE LAB SUB-SECTION ============
function VoiceLabSection({ user, onVoiceCreated, onNavigate, subscription }) {
  const [voiceProfile, setVoiceProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true); // prevents intro flash while fetching
  const [isCloning, setIsCloning] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  const [sessionBlobs, setSessionBlobs] = useState({ 1: null, 2: null, 3: null, 4: null });
  const [activeSessionId, setActiveSessionId] = useState(null);
  // sessionPhase removed — script and recorder are on the same screen
  const [tempBlob, setTempBlob] = useState(null);
  const [postSessionId, setPostSessionId] = useState(null); // shows next-step screen after saving
  const [showNameStep, setShowNameStep] = useState(false);  // name input before creating voice
  const [voiceRevealed, setVoiceRevealed] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmLabel, destructive, onConfirm }
  const audioRef = useRef(new Audio());
  const previewAudioRef = useRef(null);

  const completedCount = VOICE_SESSIONS.filter(s => sessionBlobs[s.id]).length;
  const nextSessionId = VOICE_SESSIONS.find(s => !sessionBlobs[s.id])?.id ?? null;

  useEffect(() => {
    if (!user?.user_id) return;
    loadVoiceProfile();
    // Restore any previously saved session blobs from IndexedDB
    const restoreBlobs = async () => {
      const restored = {};
      await Promise.all(VOICE_SESSIONS.map(async s => {
        const blob = await AudioStorage.load(user.user_id, s.id);
        if (blob) restored[s.id] = blob;
      }));
      if (Object.keys(restored).length > 0) {
        setSessionBlobs(prev => ({ ...prev, ...restored }));
        const count = Object.keys(restored).length;
        toast.success(`${count} session${count > 1 ? 's' : ''} recovered — your recordings are safe ✓`);
      }
    };
    restoreBlobs();
  }, [user]); // eslint-disable-line

  const loadVoiceProfile = async () => {
    try {
      const response = await axios.get(`${API}/voice-profiles/${user.user_id}/active`);
      setVoiceProfile(response.data);
    } catch (error) {
      console.error('Error loading voice profile:', error);
    } finally {
      setIsProfileLoading(false);
    }
  };

  // ── Auto-poll ElevenLabs every 10 min while voice is processing ──────────
  useEffect(() => {
    if (!voiceProfile || voiceProfile.status !== 'processing' || !user?.user_id) return;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/voice/status/${user.user_id}`);
        const newStatus = res.data.cloned_voice_status;
        if (newStatus === 'ready') {
          setVoiceProfile(prev => prev ? { ...prev, status: 'ready' } : prev);
          toast.success('🎙️ Your voice is ready! You can now create meditations with your own voice.', { duration: 8000 });
        } else if (newStatus === 'error') {
          setVoiceProfile(prev => prev ? { ...prev, status: 'error' } : prev);
          toast.error('Voice training failed. Please delete and try again.');
        }
      } catch { /* silent — don't interrupt the user */ }
    };

    poll(); // check immediately on mount
    const interval = setInterval(poll, 10 * 60 * 1000); // then every 10 min
    return () => clearInterval(interval);
  }, [voiceProfile?.status, user?.user_id]); // eslint-disable-line

  // Manage preview URL from tempBlob
  useEffect(() => {
    if (!tempBlob || !previewAudioRef.current) return;
    const url = URL.createObjectURL(tempBlob);
    previewAudioRef.current.src = url;
    setIsPreviewPlaying(false);
    return () => URL.revokeObjectURL(url);
  }, [tempBlob]);

  const handleSessionRecorded = (sessionId, blob) => {
    setSessionBlobs(prev => ({ ...prev, [sessionId]: blob }));
    setTempBlob(null);
    setActiveSessionId(null);
    if (user?.user_id) AudioStorage.save(user.user_id, sessionId, blob);
    setPostSessionId(sessionId); // show next-step screen
  };

  const handleCloneVoice = async () => {
    if (!voiceName.trim()) { toast.error('Give your voice a name first'); return; }
    if (completedCount === 0) { toast.error('Record at least one session first'); return; }

    setIsCloning(true);
    try {
      const formData = new FormData();
      formData.append('user_id', user.user_id);
      formData.append('voice_name', voiceName);
      VOICE_SESSIONS.forEach(s => {
        if (sessionBlobs[s.id]) {
          formData.append('audio_files', sessionBlobs[s.id], `session_${s.id}.webm`);
        }
      });

      const response = await axios.post(`${API}/voice-profiles/clone`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // 3 min — large audio files need time
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded / e.total) * 100);
          if (pct < 100) toast.loading(`Uploading… ${pct}%`, { id: 'voice-upload' });
          else toast.loading('Creating your voice…', { id: 'voice-upload' });
        },
      });
      toast.dismiss('voice-upload');

      // Clear persisted blobs — voice is now on the server
      if (user?.user_id) AudioStorage.clearUser(user.user_id);

      const voiceStatus = response.data.status || 'processing';
      setVoiceProfile({
        id: response.data.profile_id,
        voice_id: response.data.voice_id,
        voice_name: voiceName,
        sessions_recorded: response.data.sessions_recorded,
        status: voiceStatus,
        created_at: new Date().toISOString()
      });

      if (voiceStatus === 'processing') {
        toast.success('Recordings submitted. Your voice will be ready in 3–6 hours.', { duration: 6000 });
      } else {
        setVoiceRevealed(true);
        if (onVoiceCreated) onVoiceCreated();
      }
    } catch (error) {
      toast.dismiss('voice-upload');
      const msg = error.code === 'ECONNABORTED'
        ? 'Upload timed out — check your connection and try again'
        : (error.response?.data?.detail || 'Failed to create voice. Try again.');
      toast.error(msg);
    } finally {
      setIsCloning(false);
    }
  };

  const handleTestVoice = async () => {
    if (!voiceProfile?.voice_id) return;
    if (voiceProfile?.status === 'processing') {
      toast.error('Your voice is still processing. Try again in a few hours.'); return;
    }
    setIsTesting(true);
    try {
      const response = await axios.post(`${API}/voice-profiles/test-voice`, null, {
        params: { user_id: user.user_id, voice_id: voiceProfile.voice_id }
      });
      const audio = new Audio(response.data.audio_url);
      audio.onended = () => setIsTesting(false);
      audio.play().catch(() => { toast.error('Could not play audio'); setIsTesting(false); });
    } catch (error) {
      toast.error('Failed to test voice');
      setIsTesting(false);
    }
  };

  const handleDeleteVoice = () => {
    setConfirmModal({
      title: 'Delete your voice?',
      message: 'This will permanently remove your voice clone and all recordings. This cannot be undone.',
      confirmLabel: 'Delete voice',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/voice-profiles/${voiceProfile.id}`, { params: { user_id: user.user_id } });
          setVoiceProfile(null);
          setSessionBlobs({ 1: null, 2: null, 3: null, 4: null });
          if (user?.user_id) AudioStorage.clearUser(user.user_id);
          toast.success('Voice deleted');
        } catch (error) {
          toast.error('Failed to delete voice. Try again.');
        }
      },
    });
  };

  const rippleStyle = (size, delay) => ({
    position: 'absolute', top: '50%', left: '50%',
    width: `${size}px`, height: `${size}px`, borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.45)',
    animation: `rippleOut 2.8s ease-out ${delay}s infinite`,
  });

  // ── Screen: Voice Revealed ────────────────────────────────────────────────
  if (voiceRevealed && voiceProfile) {
    return (
      <div style={{ background: 'linear-gradient(160deg, #0D2B2A 0%, #1E6B68 100%)', borderRadius: '20px', padding: '48px 28px', textAlign: 'center', overflow: 'hidden' }}>
        <style>{`@keyframes rippleOut{0%{transform:translate(-50%,-50%) scale(0.05);opacity:.9}100%{transform:translate(-50%,-50%) scale(1);opacity:0}}`}</style>
        <div style={{ position: 'relative', width: '100px', height: '100px', margin: '0 auto 28px' }}>
          {[0, 0.7, 1.4, 2.1].map((d, i) => <div key={i} style={rippleStyle(90, d)} />)}
          <div style={{ position: 'absolute', top: '50%', left: '50%', width: '12px', height: '12px', transform: 'translate(-50%,-50%)', borderRadius: '50%', background: 'rgba(255,255,255,0.9)', boxShadow: '0 0 16px rgba(255,255,255,0.5)' }} />
        </div>
        <p style={{ margin: '0 0 6px', fontSize: '0.65rem', fontWeight: '700', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Your voice is alive</p>
        <h2 style={{ margin: '0 0 12px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.6rem', fontWeight: '400' }}>{voiceProfile.voice_name}</h2>
        {voiceProfile.sessions_recorded > 1 && (
          <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
            Built from {voiceProfile.sessions_recorded} recording sessions
          </p>
        )}
        <p style={{ margin: '0 0 28px', color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: '320px', marginLeft: 'auto', marginRight: 'auto' }}>
          Your voice is now part of every practice you create. It will guide you in ways no other voice can — because it already knows you.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
          <button onClick={handleTestVoice} disabled={isTesting} style={{ padding: '14px 32px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50px', color: '#fff', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' }}>
            {isTesting ? 'Playing…' : '▶ Hear yourself'}
          </button>
          <button onClick={() => { setVoiceRevealed(false); if (onNavigate) onNavigate('create'); }} style={{ padding: '14px 32px', background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '50px', color: '#fff', fontSize: '0.92rem', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.04em' }}>
            Create my first meditation →
          </button>
        </div>
      </div>
    );
  }

  // ── Loading skeleton — prevents any intro/paywall flash while fetching profile ──
  if (isProfileLoading) {
    return (
      <div style={{ padding: '32px 0', textAlign: 'center', color: 'rgba(30,107,104,0.35)', fontSize: '0.85rem' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', border: '2px solid rgba(30,107,104,0.2)', borderTopColor: '#1E6B68', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── Screen: Ceremony Intro ────────────────────────────────────────────────
  // Gate: voice cloning requires Pro. Users with an existing profile always have access.
  if (!voiceProfile && subscription && !subscription.loading && !subscription.isPro) {
    return (
      <PaywallCard
        feature="Voice Lab"
        reason="Clone your voice and generate meditations narrated by you. Record 4 short sessions — your voice becomes your most powerful regulation tool."
        onUpgrade={subscription.startCheckout}
      />
    );
  }

  // ── Screen: Post-session next-step ──────────────────────────────────────
  if (!voiceProfile && postSessionId !== null) {
    const nextSess = VOICE_SESSIONS.find(s => s.id === postSessionId + 1);
    const allDone = completedCount === 4;
    return (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        {/* Check mark */}
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #1E6B68, #2D8B87)', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', boxShadow: '0 6px 24px rgba(30,107,104,0.3)' }}>
          ✓
        </div>
        <p style={{ margin: '0 0 6px', fontSize: '0.65rem', fontWeight: '700', color: '#2D8B87', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Session {postSessionId} complete
        </p>
        <h2 style={{ margin: '0 0 12px', fontFamily: "'Playfair Display', serif", color: '#1E3A3A', fontSize: '1.45rem', fontWeight: '400' }}>
          {allDone ? 'All four sessions recorded.' : 'Your voice is already capturing something real.'}
        </h2>
        <p style={{ margin: '0 0 32px', color: '#888', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: '320px', marginLeft: 'auto', marginRight: 'auto' }}>
          {allDone
            ? 'Every session is complete. Your voice will be exceptionally natural — time to bring it to life.'
            : 'Each additional session gives ElevenLabs more of your voice to learn from. More sessions means more natural, more you.'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
          {/* Primary: continue to next session */}
          {nextSess && (
            <button
              onClick={() => { setPostSessionId(null); setActiveSessionId(nextSess.id); setTempBlob(null); }}
              style={{ padding: '16px 36px', width: '100%', maxWidth: '320px', background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)', color: '#fff', border: 'none', borderRadius: '50px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 16px rgba(30,107,104,0.3)' }}
            >
              Continue → Session {nextSess.id}: {nextSess.title}
              <span style={{ display: 'block', fontSize: '0.72rem', fontWeight: '400', opacity: 0.75, marginTop: '2px' }}>{nextSess.duration}</span>
            </button>
          )}

          {/* Secondary: create voice now */}
          <button
            onClick={() => { setPostSessionId(null); setShowNameStep(true); }}
            style={{ padding: '14px 32px', width: '100%', maxWidth: '320px', background: 'rgba(30,107,104,0.07)', color: '#1E6B68', border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: '50px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' }}
          >
            {allDone ? 'Create my voice →' : `Create my voice now (${completedCount} session${completedCount > 1 ? 's' : ''})`}
          </button>

          {/* Tertiary: back to overview */}
          {!allDone && (
            <button onClick={() => setPostSessionId(null)} style={{ background: 'none', border: 'none', color: '#bbb', fontSize: '0.82rem', cursor: 'pointer', padding: '4px' }}>
              Back to sessions
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Screen: Name your voice ──────────────────────────────────────────────
  if (!voiceProfile && showNameStep) {
    return (
      <div>
        <button onClick={() => setShowNameStep(false)} style={{ background: 'none', border: 'none', color: '#1E6B68', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '20px', padding: 0 }}>
          ← Back
        </button>
        <div style={{ padding: '32px 28px', background: 'rgba(255,255,255,0.95)', borderRadius: '20px', boxShadow: '0 4px 20px rgba(30,107,104,0.1)', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎙️</div>
          <h2 style={{ margin: '0 0 8px', fontFamily: "'Playfair Display', serif", color: '#1E3A3A', fontSize: '1.4rem', fontWeight: '400' }}>
            Give your voice a name
          </h2>
          <p style={{ margin: '0 0 28px', color: '#999', fontSize: '0.88rem', lineHeight: 1.6 }}>
            Something that feels like you. This is how it will appear in your meditations.
          </p>
          <input
            type="text"
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && voiceName.trim() && !isCloning && handleCloneVoice()}
            placeholder="e.g., My Calm Voice"
            autoFocus
            style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: '2px solid #B8D3CF', fontSize: '1rem', marginBottom: '20px', boxSizing: 'border-box', textAlign: 'center', fontFamily: "'Playfair Display', serif" }}
          />
          <button
            onClick={handleCloneVoice}
            disabled={!voiceName.trim() || isCloning}
            style={{ width: '100%', padding: '16px', background: voiceName.trim() && !isCloning ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)' : '#ccc', color: '#fff', border: 'none', borderRadius: '50px', fontSize: '1rem', fontWeight: '600', cursor: voiceName.trim() && !isCloning ? 'pointer' : 'not-allowed', boxShadow: voiceName.trim() ? '0 4px 16px rgba(30,107,104,0.3)' : 'none' }}
          >
            {isCloning ? 'Creating your voice…' : `Create My Voice — ${completedCount} session${completedCount > 1 ? 's' : ''}`}
          </button>
          <p style={{ margin: '12px 0 0', color: '#ccc', fontSize: '0.75rem' }}>
            You can always come back and add more sessions later to improve quality.
          </p>
        </div>
      </div>
    );
  }

  // ── Screen: Active Session — read & record simultaneously ───────────────
  if (!voiceProfile && activeSessionId) {
    const session = VOICE_SESSIONS.find(s => s.id === activeSessionId);
    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={() => { setActiveSessionId(null); setTempBlob(null); }}
            style={{ background: 'none', border: 'none', color: '#1E6B68', cursor: 'pointer', fontSize: '0.95rem', padding: '4px 8px', borderRadius: '8px' }}
          >
            ← Back
          </button>
          <div>
            <p style={{ margin: 0, fontSize: '0.65rem', fontWeight: '700', color: '#2D8B87', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Session {session.id} of 4 · {session.duration}
            </p>
            <h3 style={{ margin: '2px 0 0', color: '#1E6B68', fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', fontWeight: '400' }}>
              {session.title} — {session.subtitle}
            </h3>
          </div>
        </div>

        {/* Instruction */}
        <div style={{ padding: '12px 16px', background: 'rgba(30,107,104,0.06)', borderRadius: '10px', borderLeft: '3px solid #1E6B68', marginBottom: '16px' }}>
          <p style={{ margin: 0, color: '#1E6B68', fontSize: '0.85rem', fontStyle: 'italic' }}>{session.instruction}</p>
        </div>

        {/* Script — scrollable, stays visible throughout */}
        <div style={{ padding: '22px 20px', background: 'rgba(255,255,255,0.95)', borderRadius: '14px', marginBottom: '16px', boxShadow: '0 2px 12px rgba(30,107,104,0.08)', maxHeight: '40vh', overflowY: 'auto' }}>
          <p style={{ margin: '0 0 12px', fontSize: '0.6rem', fontWeight: '700', color: '#2D8B87', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Read aloud as you record ↓
          </p>
          <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '0.95rem', color: '#1E3A3A', lineHeight: 2.0, margin: 0, whiteSpace: 'pre-line' }}>
            {session.script}
          </p>
        </div>

        {/* Recorder */}
        <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 12px rgba(30,107,104,0.08)' }}>
          <AudioRecorder
            onRecordingComplete={(blob) => setTempBlob(blob)}
            maxDuration={[1, 4].includes(session.id) ? 360 : 660}
            autoUpload={false}
            hideSaveButton={true}
          />
          {tempBlob && (
            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
              {/* Hidden audio element for preview */}
              <audio
                ref={previewAudioRef}
                onEnded={() => setIsPreviewPlaying(false)}
                style={{ display: 'none' }}
              />
              {/* Preview button */}
              <button
                onClick={() => {
                  if (!previewAudioRef.current) return;
                  if (isPreviewPlaying) {
                    previewAudioRef.current.pause();
                    previewAudioRef.current.currentTime = 0;
                    setIsPreviewPlaying(false);
                  } else {
                    previewAudioRef.current.play().catch(() => {});
                    setIsPreviewPlaying(true);
                  }
                }}
                style={{ padding: '10px 28px', background: isPreviewPlaying ? 'rgba(30,107,104,0.15)' : 'rgba(30,107,104,0.06)', color: '#1E6B68', border: '1.5px solid rgba(30,107,104,0.3)', borderRadius: '20px', fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px' }}
              >
                {isPreviewPlaying
                  ? <><span style={{ animation: 'pulse 1s infinite', display: 'inline-block' }}>●</span> Listening…</>
                  : <>▶ Preview recording</>}
              </button>
              <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
              {/* Action row */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => { setTempBlob(null); setIsPreviewPlaying(false); }}
                  style={{ padding: '12px 24px', background: 'rgba(184,211,207,0.4)', color: '#1E6B68', border: '2px solid #B8D3CF', borderRadius: '24px', fontSize: '0.9rem', cursor: 'pointer' }}
                >
                  Re-record
                </button>
                <button
                  onClick={() => { setIsPreviewPlaying(false); handleSessionRecorded(session.id, tempBlob); }}
                  style={{ padding: '12px 28px', background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)', color: '#fff', border: 'none', borderRadius: '24px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(30,107,104,0.3)' }}
                >
                  Save Session {session.id} ✓
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Screen: Sessions Overview / Voice Management ──────────────────────────
  return (
    <div>
      {confirmModal && <ConfirmModal {...confirmModal} onClose={() => setConfirmModal(null)} />}

      {voiceProfile ? (
        /* Voice exists — management panel */
        <div>
          {/* Processing state — PVC fine-tuning in progress */}
          {voiceProfile.status === 'processing' ? (
            <div style={{ padding: '28px 24px', background: 'linear-gradient(135deg, rgba(30,107,104,0.08) 0%, rgba(45,139,135,0.05) 100%)', borderRadius: '16px', marginBottom: '24px', border: '1px solid rgba(30,107,104,0.2)', textAlign: 'center' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid rgba(30,107,104,0.2)', borderTopColor: '#1E6B68', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              <h3 style={{ margin: '0 0 8px', color: '#1E6B68', fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', fontWeight: '400' }}>
                {voiceProfile.voice_name} is being created
              </h3>
              <p style={{ margin: '0 0 6px', color: '#555', fontSize: '0.88rem', lineHeight: 1.6 }}>
                ElevenLabs is fine-tuning your professional voice.<br />
                This process takes <strong>3–6 hours</strong>.
              </p>
              <p style={{ margin: '0 0 20px', color: '#999', fontSize: '0.78rem' }}>
                {voiceProfile.sessions_recorded} sessions recorded · Created {new Date(voiceProfile.created_at).toLocaleDateString('en-GB')}
              </p>
              <button onClick={handleDeleteVoice} style={{ padding: '10px 20px', background: 'transparent', color: '#D9534F', border: '1px solid rgba(217,83,79,0.4)', borderRadius: '20px', cursor: 'pointer', fontSize: '0.8rem' }}>
                Cancel & start over
              </button>
            </div>
          ) : (
            /* Ready state */
            <div style={{ padding: '24px', background: 'linear-gradient(135deg, rgba(76,175,80,0.08) 0%, rgba(30,107,104,0.05) 100%)', borderRadius: '16px', marginBottom: '24px', border: '1px solid rgba(76,175,80,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', background: '#4CAF50', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1rem' }}>✓</div>
                <div>
                  <h3 style={{ margin: 0, color: '#1E6B68', fontSize: '1.15rem', fontFamily: "'Playfair Display', serif" }}>{voiceProfile.voice_name}</h3>
                  <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: '#888' }}>
                    {voiceProfile.sessions_recorded ? `${voiceProfile.sessions_recorded} sessions recorded · ` : ''}
                    Created {new Date(voiceProfile.created_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button onClick={handleTestVoice} disabled={isTesting} style={{ padding: '12px 24px', background: '#1E6B68', color: '#fff', border: 'none', borderRadius: '20px', cursor: 'pointer', opacity: isTesting ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Headphones size={15} color="#fff" /> {isTesting ? 'Playing…' : 'Test my voice'}
                </button>
                <button onClick={handleDeleteVoice} style={{ padding: '12px 24px', background: 'rgba(217,83,79,0.08)', color: '#D9534F', border: '1px solid #D9534F', borderRadius: '20px', cursor: 'pointer' }}>
                  Delete voice
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Session cards — intro banner always visible at top */
        <div>
          {/* Permanent intro banner — same on every device, every visit */}
          <div style={{ background: 'linear-gradient(160deg, #0D2B2A 0%, #1E4A47 60%, #1E6B68 100%)', borderRadius: '18px', padding: '22px 20px 20px', marginBottom: '20px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
              {/* Compact ripple orb */}
              <div style={{ position: 'relative', width: '44px', height: '44px', flexShrink: 0 }}>
                <style>{`@keyframes rippleOut{0%{transform:translate(-50%,-50%) scale(0.05);opacity:.8}100%{transform:translate(-50%,-50%) scale(1);opacity:0}}`}</style>
                {[0, 0.8, 1.6].map((d, i) => <div key={i} style={rippleStyle(40, d)} />)}
                <div style={{ position: 'absolute', top: '50%', left: '50%', width: '7px', height: '7px', transform: 'translate(-50%,-50%)', borderRadius: '50%', background: 'rgba(255,255,255,0.85)' }} />
              </div>
              <div>
                <p style={{ margin: '0 0 2px', fontSize: '0.58rem', fontWeight: '700', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Voice Lab</p>
                <h2 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.2rem', fontWeight: '400', lineHeight: 1.25 }}>
                  Your Voice. Your Anchor.
                </h2>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { n: '01', text: 'No external voice can regulate you the way yours can. It carries the unique signature of your nervous system.' },
                { n: '02', text: 'Four sessions — about 30 minutes total, done once. The more you give, the more natural your voice becomes.' },
                { n: '03', text: 'This is not a setup step. It\'s the beginning of your most important practice.' },
              ].map(({ n, text }) => (
                <div key={n} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '0.58rem', fontWeight: '700', color: 'rgba(255,255,255,0.28)', flexShrink: 0, paddingTop: '3px', letterSpacing: '0.05em' }}>{n}</span>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.79rem', lineHeight: 1.55 }}>{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Progress header — ring + label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px', marginBottom: '24px', padding: '16px 20px', background: 'rgba(255,255,255,0.7)', borderRadius: '14px', border: '1px solid rgba(30,107,104,0.1)' }}>
            {/* SVG circular progress ring */}
            {(() => {
              const R = 22;
              const CIRC = 2 * Math.PI * R; // 138.23
              const filled = (completedCount / 4) * CIRC;
              const ringColor = completedCount === 4 ? '#4CAF50' : '#1E6B68';
              return (
                <svg width="60" height="60" style={{ flexShrink: 0 }}>
                  {/* Track */}
                  <circle cx="30" cy="30" r={R} fill="none" stroke="rgba(30,107,104,0.1)" strokeWidth="4.5" />
                  {/* Fill arc */}
                  <circle
                    cx="30" cy="30" r={R}
                    fill="none"
                    stroke={ringColor}
                    strokeWidth="4.5"
                    strokeLinecap="round"
                    strokeDasharray={`${filled} ${CIRC}`}
                    transform="rotate(-90 30 30)"
                    style={{ transition: 'stroke-dasharray 0.5s ease' }}
                  />
                  {/* 4 tick marks at 0°, 90°, 180°, 270° */}
                  {[0, 1, 2, 3].map(i => {
                    const angle = (i / 4) * 2 * Math.PI - Math.PI / 2;
                    const x1 = 30 + (R - 6) * Math.cos(angle);
                    const y1 = 30 + (R - 6) * Math.sin(angle);
                    const x2 = 30 + (R + 1) * Math.cos(angle);
                    const y2 = 30 + (R + 1) * Math.sin(angle);
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.9)" strokeWidth="2" />;
                  })}
                  {/* Counter */}
                  <text x="30" y="26" textAnchor="middle" fontSize="13" fontWeight="700" fill={ringColor} fontFamily="system-ui, sans-serif">{completedCount}/4</text>
                  <text x="30" y="38" textAnchor="middle" fontSize="8.5" fill="#AAB8B6" fontFamily="system-ui, sans-serif" letterSpacing="0.03em">sessions</text>
                </svg>
              );
            })()}
            {/* Label block */}
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 3px', color: '#1E3A39', fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', fontWeight: '500' }}>
                Your Recording Sessions
              </h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#8FA3A0', lineHeight: 1.5 }}>
                {completedCount === 0 && 'Start with Session 1 — takes about 5 minutes.'}
                {completedCount === 1 && 'Good start. Each session makes your voice more natural.'}
                {completedCount === 2 && 'Halfway. Sessions 3 and 4 have the most impact on quality.'}
                {completedCount === 3 && 'Almost done. Session 4 is the shortest — just 5 minutes.'}
                {completedCount === 4 && '✓ All sessions complete. Your voice will be exceptionally natural.'}
              </p>
            </div>
          </div>

          {/* Session cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px' }}>
            {VOICE_SESSIONS.map(session => {
              const done = !!sessionBlobs[session.id];
              const isNext = !done && session.id === nextSessionId;
              return (
                <button
                  key={session.id}
                  onClick={() => { setActiveSessionId(session.id); setTempBlob(null); }}
                  style={{
                    padding: '18px 16px', textAlign: 'left', cursor: 'pointer',
                    background: done
                      ? 'linear-gradient(135deg, rgba(30,107,104,0.1) 0%, rgba(45,139,135,0.07) 100%)'
                      : isNext ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.75)',
                    border: done ? '1.5px solid rgba(30,107,104,0.3)' : isNext ? '2px solid #1E6B68' : '1.5px solid rgba(184,211,207,0.4)',
                    borderRadius: '14px',
                    boxShadow: isNext ? '0 4px 16px rgba(30,107,104,0.15)' : '0 2px 8px rgba(30,107,104,0.05)',
                    transition: 'all 0.18s ease',
                    opacity: !done && !isNext ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.62rem', fontWeight: '700', color: done ? '#1E6B68' : isNext ? '#1E6B68' : '#bbb', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Session {session.id}
                    </span>
                    {done
                      ? <span style={{ fontSize: '0.68rem', background: '#1E6B68', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>✓</span>
                      : isNext
                        ? <span style={{ fontSize: '0.68rem', background: 'rgba(30,107,104,0.1)', color: '#1E6B68', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>→ Next</span>
                        : <span style={{ fontSize: '0.68rem', color: '#ccc' }}>{session.duration}</span>
                    }
                  </div>
                  <p style={{ margin: '0 0 2px', color: '#1E3A3A', fontWeight: '600', fontSize: '0.92rem' }}>{session.title}</p>
                  <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>{session.subtitle}</p>
                </button>
              );
            })}
          </div>

          {/* Create voice CTA */}
          {completedCount > 0 && (
            <div style={{ padding: '20px 24px', background: 'rgba(255,255,255,0.92)', borderRadius: '16px', boxShadow: '0 2px 12px rgba(30,107,104,0.08)', textAlign: 'center' }}>
              <p style={{ margin: '0 0 14px', color: '#666', fontSize: '0.88rem', lineHeight: 1.6 }}>
                {completedCount === 4
                  ? 'All sessions complete. Your voice will be exceptionally natural.'
                  : `${completedCount} session${completedCount > 1 ? 's' : ''} recorded. You can create your voice now or continue for even better quality.`}
              </p>
              <button
                onClick={() => setShowNameStep(true)}
                style={{ padding: '14px 32px', background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)', color: '#fff', border: 'none', borderRadius: '50px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 16px rgba(30,107,104,0.25)' }}
              >
                Create My Voice →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Privacy Notice */}
      <div style={{ marginTop: '24px', padding: '14px 16px', background: 'rgba(184,211,207,0.1)', borderRadius: '10px', fontSize: '0.82rem', color: '#888' }}>
        <strong style={{ color: '#1E6B68' }}>Privacy:</strong> Your voice is used only within HYPSEYA for your personal meditations. We never share or use your recordings to train external models.
      </div>
    </div>
  );
}


// ============ SCRIPT READING VIEW ============
// Renders the generated script as clean, calm prose — pause tokens become
// visual breathing space and delivery tags become subtle labels, so the
// user never has to read machine markup like [PAUSE 6s].
const SCRIPT_TOKEN_RE = /(\[PAUSE \d+s\]|\[whispers\]|\[sighs\]|\[breaths\]|\[slow\])/g;
const SCRIPT_TOKEN_TEST = /^(\[PAUSE \d+s\]|\[whispers\]|\[sighs\]|\[breaths\]|\[slow\])$/;
const TAG_LABELS = { '[whispers]': 'whispered', '[slow]': 'slow', '[sighs]': 'sigh', '[breaths]': 'breath' };

function ScriptReadingView({ script }) {
  const lines = script.split('\n').map(l => l.trim()).filter(Boolean);
  return (
    <div style={{
      padding: '24px 22px', borderRadius: '12px',
      background: 'linear-gradient(160deg, rgba(30,107,104,0.03) 0%, rgba(184,211,207,0.08) 100%)',
      border: '1px solid rgba(184,211,207,0.4)',
      maxHeight: '420px', overflowY: 'auto',
    }}>
      {lines.map((line, li) => {
        const parts = line.split(SCRIPT_TOKEN_RE).filter(Boolean);
        // Lines that are only pause/breath events render as centered space
        const onlyTokens = parts.every(p => SCRIPT_TOKEN_TEST.test(p) || !p.trim());
        return (
          <p key={li} style={{
            margin: '0 0 14px', fontFamily: "'Georgia', serif",
            fontSize: '0.98rem', lineHeight: 2, color: '#3A4F4D',
            textAlign: onlyTokens ? 'center' : 'left',
          }}>
            {parts.map((part, pi) => {
              const pauseMatch = part.match(/^\[PAUSE (\d+)s\]$/);
              if (pauseMatch) {
                return (
                  <span key={pi} style={{
                    display: 'inline-block', margin: '0 10px',
                    color: '#9FBEBA', fontSize: '0.72rem',
                    letterSpacing: '3px', fontFamily: 'sans-serif',
                    verticalAlign: 'middle', whiteSpace: 'nowrap',
                  }}>· · ·<span style={{ letterSpacing: 0, marginLeft: 5, fontSize: '0.66rem' }}>{pauseMatch[1]}s</span></span>
                );
              }
              const label = TAG_LABELS[part];
              if (label) {
                return (
                  <span key={pi} style={{
                    display: 'inline-block', margin: '0 6px 0 0',
                    padding: '1px 8px', borderRadius: '8px',
                    background: 'rgba(30,107,104,0.07)', color: '#6A8F8C',
                    fontSize: '0.62rem', fontFamily: 'sans-serif',
                    fontWeight: '600', letterSpacing: '0.06em',
                    textTransform: 'uppercase', verticalAlign: 'middle',
                  }}>{label}</span>
                );
              }
              return <span key={pi}>{part}</span>;
            })}
          </p>
        );
      })}
    </div>
  );
}

// Friendly labels for live synthesis progress stages
const SYNTH_STAGE_LABELS = {
  preparing:  'Preparing your meditation…',
  voicing:    'Giving voice to your script…',
  composing:  'Composing the silences…',
  finalizing: 'Tuning the final audio…',
  done:       'Almost there…',
};

// ============ CREATE MEDITATION SUB-SECTION ============
function CreateMeditationSection({ user, hasVoice, onMeditationCreated, onNavigate, subscription }) {
  const [formData, setFormData] = useState({
    title: '',
    user_name: user?.name || '',
    family_members: [],
    relationship_situation: '',

    religion: 'none',
    spiritual_alignment: 'secular',
    tone: 'calm',
    length_minutes: 8,
    focus: 'stress',
    custom_focus: '',
    emotional_context: '',
    intention_objective: '',
    avoid_trauma_language: true,
    language: 'en'
  });

  const [script, setScript] = useState('');
  const [meditationId, setMeditationId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [status, setStatus] = useState('new'); // new, draft, script_ready, audio_ready
  const [audioUrl, setAudioUrl] = useState(null); // inline player after synthesis
  const [scriptView, setScriptView] = useState('read'); // 'read' | 'edit'
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [synthProgress, setSynthProgress] = useState(null); // {stage, pct} while synthesizing

  // Custom audio player state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
    else { audioRef.current.play().catch(() => {}); setIsPlaying(true); }
  };
  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };
  // Sync audio element events → state
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onDur  = () => setDuration(el.duration || 0);
    const onEnd  = () => { setIsPlaying(false); setCurrentTime(0); };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('durationchange', onDur);
    el.addEventListener('loadedmetadata', onDur);
    el.addEventListener('ended', onEnd);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('durationchange', onDur);
      el.removeEventListener('loadedmetadata', onDur);
      el.removeEventListener('ended', onEnd);
    };
  }, []); // eslint-disable-line
  // When audioUrl changes, load it into the audio element
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;
    audioRef.current.src = audioUrl;
    audioRef.current.load();
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [audioUrl]);
  const [showCrisisScreen, setShowCrisisScreen] = useState(false);
  const [pendingGenerate, setPendingGenerate] = useState(false);
  const [voiceType, setVoiceType] = useState(hasVoice ? 'personal' : 'female_guide');
  const [wizardStep, setWizardStep] = useState(1);

  // Auto-switch to personal voice when Voice Lab is completed in the same session
  useEffect(() => {
    if (hasVoice && voiceType !== 'personal') setVoiceType('personal');
  }, [hasVoice]); // eslint-disable-line

  const [newFamilyMember, setNewFamilyMember] = useState({ name: '', relationship: '', notes: '' });

  const isCrisis = useCrisisDetection(
    (formData.emotional_context || '') + ' ' + (formData.intention_objective || '')
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addFamilyMember = () => {
    if (newFamilyMember.name && newFamilyMember.relationship) {
      setFormData(prev => ({
        ...prev,
        family_members: [...prev.family_members, { ...newFamilyMember }]
      }));
      setNewFamilyMember({ name: '', relationship: '', notes: '' });
    }
  };

  const removeFamilyMember = (index) => {
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.filter((_, i) => i !== index)
    }));
  };

  const handleGenerateScript = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a meditation title');
      return;
    }

    // Crisis check — intercept before generating any content
    if (isCrisis) {
      setPendingGenerate(true);
      setShowCrisisScreen(true);
      return;
    }

    setIsGenerating(true);
    try {
      // First create the meditation if it doesn't exist
      let medId = meditationId;
      if (!medId) {
        const createResponse = await axios.post(`${API}/meditations`, {
          ...formData,
          user_id: user.user_id
        });
        medId = createResponse.data.meditation_id;
        setMeditationId(medId);
      }

      // Generate script
      const scriptResponse = await axios.post(
        `${API}/meditations/${medId}/generate-script`,
        null,
        { params: { user_id: user.user_id } }
      );

      setScript(scriptResponse.data.script);
      setStatus('script_ready');
      const estMin = scriptResponse.data.estimated_duration_minutes;
      const targetMin = scriptResponse.data.target_minutes || formData.length_minutes;
      const wordCount = scriptResponse.data.word_count;
      toast.success(
        estMin
          ? `Script ready — estimated ${estMin} min · ${wordCount} words`
          : 'Script generated. Review and edit as needed.'
      );
    } catch (error) {
      console.error('Generate error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate script');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveScript = async () => {
    if (!meditationId || !script) return;

    try {
      await axios.put(
        `${API}/meditations/${meditationId}/script`,
        null,
        { params: { user_id: user.user_id, script } }
      );
      toast.success('Script saved');
    } catch (error) {
      toast.error('Failed to save script');
    }
  };

  // ~30s voice sample so the user can check tone before full synthesis
  const handlePreview = async () => {
    if (!meditationId || !script) { toast.error('Generate a script first'); return; }
    if (voiceType === 'personal' && !hasVoice) { toast.error('Create your voice in Voice Lab first'); return; }
    setIsPreviewing(true);
    setPreviewUrl(null);
    try {
      // Save the current script first so the sample reflects any edits
      await axios.put(`${API}/meditations/${meditationId}/script`, null,
        { params: { user_id: user.user_id, script } });
      await axios.post(`${API}/meditations/${meditationId}/preview`, null,
        { params: { user_id: user.user_id, voice_type: voiceType } });
      setPreviewUrl(`${API}/meditations/${meditationId}/preview-audio?user_id=${encodeURIComponent(user.user_id)}&t=${Date.now()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setIsPreviewing(false);
    }
  };

  // Poll live synthesis progress while the full audio is being created
  useEffect(() => {
    if (!isSynthesizing || !meditationId) { setSynthProgress(null); return; }
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/meditations/detail/${meditationId}`,
          { params: { user_id: user.user_id } });
        if (r.data?.synthesis_progress) setSynthProgress(r.data.synthesis_progress);
      } catch {}
    }, 2500);
    return () => clearInterval(iv);
  }, [isSynthesizing, meditationId]); // eslint-disable-line

  const handleSynthesize = async () => {
    // Only block if specifically using personal voice without one recorded
    if (voiceType === 'personal' && !hasVoice) {
      toast.error('Create your voice in Voice Lab first');
      return;
    }
    if (!script) {
      toast.error('Generate a script first');
      return;
    }

    setIsSynthesizing(true);
    try {
      const response = await axios.post(
        `${API}/meditations/${meditationId}/synthesize`,
        null,
        { params: { user_id: user.user_id, voice_type: voiceType } }
      );

      const data = response.data;
      setStatus('audio_ready');
      // Build audio URL — served by GET /meditations/{id}/audio
      setAudioUrl(`${API}/meditations/${meditationId}/audio?user_id=${user.user_id}`);
      // Pre-set duration from API response so the player shows it immediately
      // (mobile browsers often can't read duration from loadedmetadata on streamed MP3)
      if (data.duration_seconds) {
        setDuration(data.duration_seconds);
      }

      let successMsg = 'Meditation audio created!';
      if (data.duration_seconds) {
        const mins = Math.floor(data.duration_seconds / 60);
        const secs = Math.round(data.duration_seconds % 60);
        successMsg = `Meditation ready — ${mins}:${secs.toString().padStart(2, '0')}`;
      }
      toast.success(successMsg);

      if (onMeditationCreated) onMeditationCreated();
    } catch (error) {
      console.error('Synthesize error:', error);
      toast.error(error.response?.data?.detail || 'Failed to synthesize audio');
    } finally {
      setIsSynthesizing(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      user_name: user?.name || '',
      family_members: [],
      relationship_situation: '',
  
      religion: 'none',
      spiritual_alignment: 'secular',
      tone: 'calm',
      length_minutes: 8,
      focus: 'stress',
      custom_focus: '',
      emotional_context: '',
      intention_objective: '',
      avoid_trauma_language: true
    });
    setScript('');
    setMeditationId(null);
    setStatus('new');
    setAudioUrl(null);
  };

  const focusOptions = [
    { value: 'anxiety', label: 'Anxiety Relief' },
    { value: 'sleep', label: 'Sleep & Rest' },
    { value: 'confidence', label: 'Confidence' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'gratitude', label: 'Gratitude' },
    { value: 'stress', label: 'Stress Relief' },
    { value: 'self_love', label: 'Self Love' },
    { value: 'morning', label: 'Morning Intention' },
    { value: 'recovery', label: 'Recovery & Healing' },
    { value: 'custom', label: 'Custom Focus' }
  ];

  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '2px solid #B8D3CF', fontSize: '1rem', marginTop: '4px'
  };

  const labelStyle = {
    display: 'block', marginBottom: '4px', color: '#1E6B68',
    fontWeight: '500', fontSize: '0.9rem'
  };

  return (
    <div>
      {/* Crisis Screen — full-screen intercept */}
      {showCrisisScreen && (
        <CrisisScreen
          onContinue={pendingGenerate ? async () => {
            setShowCrisisScreen(false);
            setPendingGenerate(false);
            // User acknowledged — proceed with generation
            setIsGenerating(true);
            try {
              let medId = meditationId;
              if (!medId) {
                const createResponse = await axios.post(`${API}/meditations`, { ...formData, user_id: user.user_id });
                medId = createResponse.data.meditation_id;
                setMeditationId(medId);
              }
              const scriptResponse = await axios.post(`${API}/meditations/${medId}/generate-script`, null, { params: { user_id: user.user_id } });
              setScript(scriptResponse.data.script);
              setStatus('script_ready');
              toast.success('Script generated. Review and edit as needed.');
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Failed to generate script');
            } finally {
              setIsGenerating(false);
            }
          } : undefined}
          onDismiss={() => { setShowCrisisScreen(false); setPendingGenerate(false); }}
        />
      )}

      {/* Free usage counter / upgrade prompt */}
      {subscription && !subscription.loading && !subscription.isPro && subscription.meditationsLeft !== null && (
        subscription.meditationsLeft === 0 ? (
          <div style={{ marginBottom: '24px' }}>
            <PaywallCard
              feature="Create Meditation"
              meditationsLeft={0}
              onUpgrade={subscription.startCheckout}
            />
          </div>
        ) : (
          <div style={{ marginBottom: '20px', padding: '12px 18px', background: 'rgba(30,107,104,0.06)', borderRadius: '12px', border: '1px solid rgba(30,107,104,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <p style={{ margin: 0, color: '#1E6B68', fontSize: '0.85rem' }}>
              <strong>{subscription.meditationsLeft}</strong> free meditation{subscription.meditationsLeft !== 1 ? 's' : ''} remaining on your free plan
            </p>
            <button
              onClick={() => subscription.startCheckout(process.env.REACT_APP_STRIPE_PRICE_YEARLY || '')}
              style={{ padding: '7px 16px', background: '#1E6B68', color: '#fff', border: 'none', borderRadius: '20px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
            >
              Upgrade — 7 days free
            </button>
          </div>
        )
      )}

      {/* ── Wizard form (only shown when status is 'new') ──────────────────── */}
      {status === 'new' && (
        <div style={{ padding: '24px', background: 'rgba(255,255,255,0.9)', borderRadius: '16px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(30,107,104,0.1)' }}>

          {/* Step indicator */}
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '28px' }}>
            {[{ n: 1, label: 'Your moment' }, { n: 2, label: 'Your voice' }, { n: 3, label: 'Details' }].map((s, idx) => (
              <React.Fragment key={s.n}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: wizardStep > s.n ? '#1E6B68' : wizardStep === s.n ? 'linear-gradient(135deg,#1E6B68,#2D8B87)' : 'rgba(184,211,207,0.3)', border: wizardStep > s.n ? 'none' : wizardStep === s.n ? '2.5px solid #1E6B68' : '1.5px solid rgba(184,211,207,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: wizardStep >= s.n ? '#fff' : '#AAB8B6', fontSize: '0.78rem', fontWeight: '700', transition: 'all 0.3s ease', flexShrink: 0 }}>
                    {wizardStep > s.n ? '✓' : s.n}
                  </div>
                  <span style={{ fontSize: '0.62rem', color: wizardStep === s.n ? '#1E6B68' : '#AAB8B6', fontWeight: wizardStep === s.n ? '700' : '400', whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>{s.label}</span>
                </div>
                {idx < 2 && <div style={{ flex: 1, height: '2px', marginTop: '13px', background: wizardStep > s.n ? '#1E6B68' : 'rgba(184,211,207,0.35)', transition: 'background 0.3s ease', marginLeft: '8px', marginRight: '8px' }} />}
              </React.Fragment>
            ))}
          </div>

          {/* ── STEP 1 — Your moment ───────────────────────────────────────── */}
          {wizardStep === 1 && (
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: '700', color: '#AAB8B6', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Step 1 of 3</div>
                <h3 style={{ margin: '0 0 20px', color: '#1E3A39', fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', fontWeight: '500' }}>What do you need right now?</h3>
              </div>

              {/* Focus — pill grid instead of dropdown */}
              <div>
                <label style={labelStyle}>This meditation is for</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '8px' }}>
                  {focusOptions.filter(o => o.value !== 'custom').map(opt => (
                    <button key={opt.value} onClick={() => handleInputChange('focus', opt.value)}
                      style={{ padding: '10px 12px', borderRadius: '10px', textAlign: 'left', background: formData.focus === opt.value ? 'rgba(30,107,104,0.1)' : 'rgba(255,255,255,0.7)', border: formData.focus === opt.value ? '2px solid #1E6B68' : '1.5px solid rgba(184,211,207,0.4)', color: formData.focus === opt.value ? '#1E6B68' : '#555', fontSize: '0.83rem', fontWeight: formData.focus === opt.value ? '600' : '400', cursor: 'pointer', transition: 'all 0.15s ease' }}>
                      {opt.label}
                    </button>
                  ))}
                  <button onClick={() => handleInputChange('focus', 'custom')}
                    style={{ padding: '10px 12px', borderRadius: '10px', textAlign: 'left', background: formData.focus === 'custom' ? 'rgba(30,107,104,0.1)' : 'rgba(255,255,255,0.7)', border: formData.focus === 'custom' ? '2px solid #1E6B68' : '1.5px solid rgba(184,211,207,0.4)', color: formData.focus === 'custom' ? '#1E6B68' : '#555', fontSize: '0.83rem', cursor: 'pointer', transition: 'all 0.15s ease' }}>
                    Custom Focus
                  </button>
                </div>
                {formData.focus === 'custom' && (
                  <input type="text" value={formData.custom_focus} onChange={(e) => handleInputChange('custom_focus', e.target.value)} placeholder="Describe your focus…" style={{ ...inputStyle, marginTop: '8px' }} />
                )}
              </div>

              {/* Emotional context */}
              <div>
                <label style={labelStyle}>What are you going through right now? <span style={{ fontWeight: '400', color: '#AAB8B6' }}>(optional)</span></label>
                <textarea value={formData.emotional_context} onChange={(e) => handleInputChange('emotional_context', e.target.value)}
                  placeholder="e.g., I'm going through grief after losing someone. I feel lost and heavy."
                  rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit', borderColor: isCrisis ? '#C0392B' : undefined }} />
                {isCrisis ? (
                  <div style={{ marginTop: '8px', padding: '12px 14px', background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '10px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '1.1rem', marginTop: '1px' }}>🤍</span>
                    <div>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#8B2020', fontWeight: '600' }}>We hear you. Before we continue, please know you're not alone.</p>
                      <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#8B2020' }}>If you're in a difficult moment, speaking with someone can help. We'll show you support lines when you generate.</p>
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#B8D3CF' }}>This helps the AI write something that truly meets you where you are.</p>
                )}
              </div>

              {/* Intention */}
              <div>
                <label style={labelStyle}>What do you want to feel or receive? <span style={{ fontWeight: '400', color: '#AAB8B6' }}>(optional)</span></label>
                <textarea value={formData.intention_objective} onChange={(e) => handleInputChange('intention_objective', e.target.value)}
                  placeholder="e.g., I want to feel held and safe. I want to release this heaviness."
                  rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit' }} />
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#B8D3CF' }}>Your intention shapes the arc of the whole meditation.</p>
              </div>
            </div>
          )}

          {/* ── STEP 2 — Your voice ────────────────────────────────────────── */}
          {wizardStep === 2 && (
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: '700', color: '#AAB8B6', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Step 2 of 3</div>
                <h3 style={{ margin: '0 0 20px', color: '#1E3A39', fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', fontWeight: '500' }}>How should it sound?</h3>
              </div>

              {/* Voice selector */}
              <div>
                <label style={{ ...labelStyle, marginBottom: '10px' }}>Voice</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {[
                    { id: 'personal', icon: <Mic size={22} color="currentColor" />, label: 'My voice', sub: hasVoice ? 'Voice Lab' : 'Record first', locked: !hasVoice },
                    { id: 'female_guide', icon: <Bloom size={22} color="currentColor" />, label: 'Female guide', sub: 'Soft & serene', locked: false },
                    { id: 'male_guide', icon: <Leaf size={22} color="currentColor" />, label: 'Male guide', sub: 'Deep & calm', locked: false },
                  ].map(v => {
                    const sel = voiceType === v.id;
                    return (
                      <button key={v.id} onClick={() => !v.locked && setVoiceType(v.id)}
                        style={{ padding: '14px 8px', borderRadius: '14px', border: sel ? '2px solid #1E6B68' : '1.5px solid rgba(184,211,207,0.6)', background: sel ? 'linear-gradient(135deg,rgba(30,107,104,0.1),rgba(45,139,135,0.07))' : 'rgba(255,255,255,0.85)', cursor: v.locked ? 'not-allowed' : 'pointer', opacity: v.locked ? 0.45 : 1, textAlign: 'center', transition: 'all 0.18s ease' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px', color: sel ? '#1E6B68' : '#666' }}>{v.icon}</div>
                        <div style={{ fontSize: '0.78rem', fontWeight: '700', color: sel ? '#1E6B68' : '#333', marginBottom: '2px' }}>{v.label}</div>
                        <div style={{ fontSize: '0.65rem', color: '#999' }}>{v.sub}</div>
                        {sel && <div style={{ marginTop: '5px', fontSize: '0.6rem', fontWeight: '700', color: '#1E6B68', letterSpacing: '0.06em' }}>✓ SELECTED</div>}
                      </button>
                    );
                  })}
                </div>
                {!hasVoice && <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#aaa' }}>Record your voice in <strong style={{ color: '#1E6B68', cursor: 'pointer' }} onClick={() => onNavigate && onNavigate('voice_lab')}>Voice Lab</strong> to use your own voice.</p>}
              </div>

              {/* Length + Tone */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Length</label>
                  <select value={formData.length_minutes} onChange={(e) => handleInputChange('length_minutes', parseInt(e.target.value))} style={inputStyle}>
                    {[3,5,8,10,15,20].map(m => <option key={m} value={m}>{m} minutes</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Tone</label>
                  <select value={formData.tone} onChange={(e) => handleInputChange('tone', e.target.value)} style={inputStyle}>
                    <option value="calm">Calm</option>
                    <option value="reassuring">Reassuring</option>
                    <option value="motivating">Motivating</option>
                    <option value="grounding">Grounding</option>
                    <option value="expansive">Expansive</option>
                  </select>
                </div>
              </div>

              {/* Language */}
              <div>
                <label style={labelStyle}>Language</label>
                <select value={formData.language} onChange={(e) => handleInputChange('language', e.target.value)} style={inputStyle}>
                  <option value="en">English</option>
                  <option value="pt">Português</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                </select>
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#B8D3CF' }}>The meditation will be written and spoken in this language.</p>
              </div>

              {/* Inner frame */}
              <div>
                <label style={labelStyle}>Inner frame</label>
                <select value={formData.spiritual_alignment} onChange={(e) => handleInputChange('spiritual_alignment', e.target.value)} style={inputStyle}>
                  <option value="secular">Secular — no spiritual frame</option>
                  <option value="mindfulness">Mindfulness / Presence</option>
                  <option value="somatic">Somatic / Body & Earth</option>
                  <option value="psychedelic">Psychedelic-informed</option>
                  <option value="shamanic">Shamanic / Plant medicine</option>
                  <option value="nondual">Non-dual / Contemplative</option>
                  <option value="buddhist">Buddhist / Dharma</option>
                  <option value="stoic">Stoic / Philosophical</option>
                </select>
              </div>
            </div>
          )}

          {/* ── STEP 3 — Details ──────────────────────────────────────────── */}
          {wizardStep === 3 && (
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: '700', color: '#AAB8B6', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Step 3 of 3</div>
                <h3 style={{ margin: '0 0 4px', color: '#1E3A39', fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', fontWeight: '500' }}>A few last details</h3>
                <p style={{ margin: '0 0 20px', fontSize: '0.8rem', color: '#AAB8B6' }}>All optional except the title — you can skip the rest and generate now.</p>
              </div>

              {/* Title */}
              <div>
                <label style={labelStyle}>Meditation title *</label>
                <input type="text" value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)} placeholder="e.g., Morning Calm, Evening Release" style={inputStyle} />
              </div>

              {/* Your name */}
              <div>
                <label style={labelStyle}>Your name <span style={{ fontWeight: '400', color: '#AAB8B6' }}>(for personalisation)</span></label>
                <input type="text" value={formData.user_name} onChange={(e) => handleInputChange('user_name', e.target.value)} placeholder="How you'd like to be addressed" style={inputStyle} />
              </div>

              {/* Family members */}
              <div>
                <label style={labelStyle}>Family members <span style={{ fontWeight: '400', color: '#AAB8B6' }}>(optional)</span></label>
                <p style={{ fontSize: '0.8rem', color: '#888', margin: '0 0 10px' }}>Add names to personalise your meditation.</p>
                {formData.family_members.map((fm, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: 'rgba(184,211,207,0.2)', borderRadius: '8px', marginBottom: '8px' }}>
                    <span style={{ flex: 1, color: '#1E6B68' }}>{fm.name} ({fm.relationship})</span>
                    <button onClick={() => removeFamilyMember(idx)} style={{ background: 'none', border: 'none', color: '#D9534F', cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <input type="text" value={newFamilyMember.name} onChange={(e) => setNewFamilyMember(prev => ({ ...prev, name: e.target.value }))} placeholder="Name" style={{ ...inputStyle, flex: '1', minWidth: '120px' }} />
                  <input type="text" value={newFamilyMember.relationship} onChange={(e) => setNewFamilyMember(prev => ({ ...prev, relationship: e.target.value }))} placeholder="Relationship" style={{ ...inputStyle, flex: '1', minWidth: '120px' }} />
                  <button onClick={addFamilyMember} disabled={!newFamilyMember.name || !newFamilyMember.relationship} style={{ padding: '12px 16px', background: '#1E6B68', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: (!newFamilyMember.name || !newFamilyMember.relationship) ? 0.5 : 1 }}>+ Add</button>
                </div>
              </div>

              {/* Relationship context */}
              <div>
                <label style={labelStyle}>Relationship context <span style={{ fontWeight: '400', color: '#AAB8B6' }}>(optional)</span></label>
                <textarea value={formData.relationship_situation} onChange={(e) => handleInputChange('relationship_situation', e.target.value)} placeholder="Any context about relationships you'd like incorporated…" rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              {/* Safety toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input type="checkbox" id="avoidTrauma" checked={formData.avoid_trauma_language} onChange={(e) => handleInputChange('avoid_trauma_language', e.target.checked)} style={{ width: '20px', height: '20px' }} />
                <label htmlFor="avoidTrauma" style={{ color: '#666', fontSize: '0.9rem' }}>Avoid intense trauma language (recommended)</label>
              </div>
            </div>
          )}

          {/* Step navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '28px', gap: '12px' }}>
            {wizardStep > 1
              ? <button onClick={() => setWizardStep(s => s - 1)} style={{ padding: '12px 24px', background: 'transparent', color: '#1E6B68', border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: '24px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: '500' }}>← Back</button>
              : <div />
            }
            {wizardStep < 3
              ? (
                <button
                  onClick={() => {
                    if (wizardStep === 2 && !formData.title) {
                      const FOCUS_TITLES = { anxiety: 'Releasing Anxiety', sleep: 'Gentle Rest', confidence: 'Coming Home to Yourself', grief: 'With My Grief', gratitude: 'Gratitude Practice', stress: 'A Moment of Stillness', self_love: 'Returning to Myself', morning: 'Morning Intention', recovery: 'Healing Space', custom: 'My Meditation' };
                      handleInputChange('title', FOCUS_TITLES[formData.focus] || 'My Meditation');
                    }
                    setWizardStep(s => s + 1);
                  }}
                  style={{ padding: '12px 28px', background: 'linear-gradient(135deg,#1E6B68,#2D8B87)', color: '#fff', border: 'none', borderRadius: '24px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(30,107,104,0.2)' }}
                >Continue →</button>
              ) : (
                <button onClick={handleGenerateScript} disabled={isGenerating || !formData.title.trim()}
                  style={{ padding: '14px 32px', background: isGenerating ? '#ccc' : 'linear-gradient(135deg,#1E6B68,#2D8B87)', color: '#fff', border: 'none', borderRadius: '24px', fontSize: '1rem', fontWeight: '600', cursor: isGenerating ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(30,107,104,0.3)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><line x1="10" y1="2" x2="10" y2="5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/><line x1="10" y1="15" x2="10" y2="18" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/><line x1="2" y1="10" x2="5" y2="10" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/><line x1="15" y1="10" x2="18" y2="10" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/><circle cx="10" cy="10" r="2" fill="#fff"/></svg>
                  {isGenerating ? 'Generating script…' : 'Generate Script →'}
                </button>
              )
            }
          </div>
        </div>
      )}

      {/* Compact summary bar when script/audio is ready */}
      {status !== 'new' && (
        <div style={{ marginBottom: '16px', padding: '14px 18px', background: 'rgba(30,107,104,0.05)', borderRadius: '12px', border: '1px solid rgba(30,107,104,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#2D8B87', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{status === 'audio_ready' ? '✓ Audio ready' : '✓ Script ready'}</span>
            <p style={{ margin: '2px 0 0', fontSize: '0.88rem', color: '#333', fontWeight: '500' }}>{formData.title || 'Your meditation'}</p>
          </div>
          <button onClick={() => { handleReset(); setWizardStep(1); }} style={{ padding: '8px 18px', background: 'transparent', color: '#1E6B68', border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: '20px', fontSize: '0.8rem', cursor: 'pointer' }}>
            + New meditation
          </button>
        </div>
      )}

      {/* Script Section */}
      {script && (
        <div style={{
          padding: '24px', background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '16px', marginBottom: '24px',
          boxShadow: '0 4px 20px rgba(30, 107, 104, 0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <h3 style={{ color: '#1E6B68', margin: 0, fontFamily: "'Playfair Display', serif" }}>
              Your meditation script
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setScriptView(v => v === 'read' ? 'edit' : 'read')}
                style={{
                  padding: '5px 14px', background: 'transparent', color: '#2D8B87',
                  border: '1.5px solid rgba(45,139,135,0.3)', borderRadius: '14px',
                  fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer'
                }}
              >{scriptView === 'read' ? 'Edit script' : 'Reading view'}</button>
              <span style={{
                padding: '4px 12px', background: status === 'audio_ready' ? '#4CAF50' : '#FFC107',
                color: '#fff', borderRadius: '12px', fontSize: '0.8rem'
              }}>
                {status === 'audio_ready' ? 'Audio Ready' : 'Script Ready'}
              </span>
            </div>
          </div>

          {scriptView === 'read' ? (
            <ScriptReadingView script={script} />
          ) : (
            <>
              <textarea
                value={script}
                onChange={(e) => setScript(e.target.value)}
                rows={15}
                style={{
                  width: '100%', padding: '16px', borderRadius: '12px',
                  border: '2px solid #B8D3CF', fontSize: '1rem',
                  fontFamily: "'Georgia', serif", lineHeight: '1.8',
                  resize: 'vertical', minHeight: '300px'
                }}
              />
              <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#AAB8B6' }}>
                Advanced editing — keep the [PAUSE Xs] tokens and tags like [whispers] intact; they control the pacing and delivery of the audio.
              </p>
            </>
          )}

          {/* 30-second voice sample */}
          <div style={{ marginTop: '16px', padding: '14px 16px', background: 'rgba(184,211,207,0.12)', borderRadius: '12px', border: '1px solid rgba(184,211,207,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
              <div>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#1E6B68' }}>Hear a 30-second sample first</p>
                <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#8FA3A0' }}>Check the voice and pacing before creating the full audio.</p>
              </div>
              <button
                onClick={handlePreview}
                disabled={isPreviewing || isSynthesizing}
                style={{
                  padding: '9px 18px', background: isPreviewing ? '#ccc' : 'rgba(30,107,104,0.1)',
                  color: '#1E6B68', border: '1.5px solid #1E6B68', borderRadius: '18px',
                  fontSize: '0.82rem', fontWeight: '600', cursor: isPreviewing ? 'wait' : 'pointer', whiteSpace: 'nowrap'
                }}
              >{isPreviewing ? 'Creating sample…' : previewUrl ? '↻ New sample' : '▶ Hear sample'}</button>
            </div>
            {previewUrl && (
              <audio controls src={previewUrl} style={{ width: '100%', marginTop: '12px', height: '36px' }} />
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={handleGenerateScript}
              disabled={isGenerating}
              style={{
                padding: '12px 24px', background: 'rgba(30, 107, 104, 0.1)',
                color: '#1E6B68', border: '2px solid #1E6B68', borderRadius: '20px', cursor: 'pointer'
              }}
            >
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><Cycle size={15} color="#1E6B68"/>Regenerate</span>
            </button>
            <button
              onClick={handleSaveScript}
              style={{
                padding: '12px 24px', background: 'rgba(30, 107, 104, 0.1)',
                color: '#1E6B68', border: '2px solid #1E6B68', borderRadius: '20px', cursor: 'pointer'
              }}
            >
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><Save size={15} color="#1E6B68"/>Save Draft</span>
            </button>
            {subscription && !subscription.isPro && subscription.meditationsLeft === 0 ? (
              <div style={{ flex: '1 1 100%', marginTop: '4px' }}>
                <PaywallCard
                  compact
                  feature="Synthesize"
                  meditationsLeft={0}
                  onUpgrade={subscription.startCheckout}
                />
              </div>
            ) : (
              <button
                onClick={handleSynthesize}
                disabled={isSynthesizing || (voiceType === 'personal' && !hasVoice)}
                style={{
                  padding: '12px 24px',
                  background: isSynthesizing ? '#ccc' : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                  color: '#fff', border: 'none', borderRadius: '20px',
                  cursor: (isSynthesizing || (voiceType === 'personal' && !hasVoice)) ? 'not-allowed' : 'pointer',
                  opacity: (voiceType === 'personal' && !hasVoice) ? 0.5 : 1
                }}
              >
                <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}>
                  <Mic size={15} color="#fff"/>
                  {isSynthesizing ? 'Synthesizing…' : voiceType === 'personal' ? 'Synthesize with my voice' : voiceType === 'female_guide' ? 'Synthesize with female voice' : 'Synthesize with male voice'}
                </span>
              </button>
            )}
          </div>

          {/* Live synthesis progress */}
          {isSynthesizing && (
            <div style={{
              marginTop: '16px', padding: '16px 18px',
              background: 'rgba(30,107,104,0.05)', borderRadius: '12px',
              border: '1px solid rgba(30,107,104,0.15)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#1E6B68' }}>
                  {SYNTH_STAGE_LABELS[synthProgress?.stage] || 'Preparing your meditation…'}
                </p>
                <span style={{ fontSize: '0.78rem', color: '#8FA3A0', fontWeight: '600' }}>
                  {Math.max(2, synthProgress?.pct || 0)}%
                </span>
              </div>
              <div style={{ height: '6px', background: 'rgba(184,211,207,0.3)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '3px',
                  width: `${Math.max(2, synthProgress?.pct || 0)}%`,
                  background: 'linear-gradient(90deg, #1E6B68, #2D8B87)',
                  transition: 'width 1.2s ease',
                }} />
              </div>
              <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: '#AAB8B6' }}>
                A longer meditation takes a few minutes — the voice, the pauses and the pacing are being composed piece by piece.
              </p>
            </div>
          )}

          {/* Meditation Mode Info */}
          <div style={{
            marginTop: '16px', padding: '12px 16px',
            background: 'linear-gradient(135deg, rgba(30, 107, 104, 0.08) 0%, rgba(184, 211, 207, 0.15) 100%)',
            borderRadius: '12px', borderLeft: '4px solid #1E6B68'
          }}>
            <p style={{ margin: 0, color: '#1E6B68', fontSize: '0.9rem', fontWeight: '500' }}>
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><Meditate size={15} color="#1E6B68"/>Meditation Mode Active</span>
            </p>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: '0.85rem' }}>
              Your meditation will be synthesised at a measured pace with natural breathing — a genuine meditative register.
            </p>
          </div>

          {!hasVoice && voiceType === 'personal' && (
            <p style={{ marginTop: '12px', color: '#D9534F', fontSize: '0.9rem' }}>
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><Alert size={14} color="#D9534F"/>Select a guide voice above, or create your voice in Voice Lab</span>
            </p>
          )}
        </div>
      )}

      {/* Post-synthesis — inline player + navigation */}
      {status === 'audio_ready' && (
        <div style={{
          marginBottom: '24px',
          padding: '28px 24px',
          background: 'linear-gradient(135deg, #0D2B2A 0%, #1E6B68 100%)',
          borderRadius: '18px',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(30,107,104,0.25)',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🎧</div>
          <h3 style={{
            margin: '0 0 6px',
            fontFamily: "'Playfair Display', serif",
            color: '#fff', fontSize: '1.35rem', fontWeight: '400'
          }}>
            Your meditation is ready
          </h3>
          <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>
            {formData.title}
          </p>

          {/* Hidden audio element — controlled via ref */}
          <audio ref={audioRef} preload="metadata" style={{ display: 'none' }} />

          {/* Custom meditation player */}
          {audioUrl ? (
            <div style={{
              background: 'rgba(0,0,0,0.25)',
              borderRadius: '14px',
              padding: '18px 20px',
              marginBottom: '20px',
            }}>
              {/* Progress bar */}
              <div
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ratio = (e.clientX - rect.left) / rect.width;
                  if (audioRef.current && duration) {
                    audioRef.current.currentTime = ratio * duration;
                    setCurrentTime(ratio * duration);
                  }
                }}
                style={{
                  width: '100%', height: '4px',
                  background: 'rgba(255,255,255,0.18)',
                  borderRadius: '4px', cursor: 'pointer',
                  marginBottom: '14px', position: 'relative',
                }}
              >
                <div style={{
                  height: '100%', borderRadius: '4px',
                  background: 'rgba(255,255,255,0.85)',
                  width: duration ? `${(currentTime / duration) * 100}%` : '0%',
                  transition: 'width 0.3s linear',
                }} />
              </div>

              {/* Controls row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
                  {formatTime(currentTime)}
                </span>

                {/* Play / Pause button */}
                <button
                  onClick={togglePlay}
                  style={{
                    width: '52px', height: '52px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.15)',
                    border: '1.5px solid rgba(255,255,255,0.5)',
                    color: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.2rem',
                    transition: 'background 0.2s',
                  }}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying
                    ? <svg width="16" height="16" viewBox="0 0 16 16" fill="white"><rect x="2" y="2" width="4" height="12" rx="1"/><rect x="10" y="2" width="4" height="12" rx="1"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 16 16" fill="white"><polygon points="3,1 15,8 3,15"/></svg>
                  }
                </button>

                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          ) : (
            <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
              Audio available in My Meditations
            </p>
          )}

          <button
            onClick={() => onNavigate && onNavigate('library')}
            style={{
              padding: '14px 36px',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: '50px', color: '#fff',
              fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
              letterSpacing: '0.04em', display: 'block',
              width: '100%', maxWidth: '280px', margin: '0 auto',
            }}
          >
            My Meditations →
          </button>

          <button
            onClick={handleReset}
            style={{
              background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem',
              cursor: 'pointer', padding: '4px', marginTop: '14px',
            }}
          >
            New Meditation
          </button>
        </div>
      )}

      {/* Start Over */}
      {(script || meditationId) && status !== 'audio_ready' && (
        <button
          onClick={handleReset}
          style={{
            padding: '12px 24px', background: 'transparent',
            color: '#666', border: '1px solid #ccc', borderRadius: '20px',
            cursor: 'pointer', fontSize: '0.9rem'
          }}
        >
          New Meditation
        </button>
      )}
    </div>
  );
}


// Demo meditations — shown when backend is unreachable
const DEMO_MEDITATIONS = [
  {
    id: 'demo-1',
    title: 'Morning Ground',
    focus: 'morning',
    duration_minutes: 8,
    status: 'audio_ready',
    audio_file_path: true,
    script: 'Begin by finding a comfortable position. Let your eyes close softly. [PAUSE 4s] Take a long breath in through your nose, filling your lungs completely. Hold at the top. [PAUSE 2s] And release slowly through your mouth. [PAUSE 6s] You are starting your day from a place of intention, not reaction. [PAUSE 4s] Feel your feet on the floor beneath you. The ground is here. You are here.',
    word_count: 68,
    created_at: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: 'demo-2',
    title: 'Anxiety Release',
    focus: 'anxiety',
    duration_minutes: 12,
    status: 'audio_ready',
    audio_file_path: true,
    script: 'Notice where you hold the tension. It is okay to feel it. [PAUSE 4s] Breathe into it — not to push it away, but to be with it. [PAUSE 6s] Your nervous system is not broken. It is responding. And right now, you are giving it permission to soften. [PAUSE 4s] With each exhale, let a little more go. You are safe. This moment is manageable.',
    word_count: 62,
    created_at: new Date(Date.now() - 5 * 86400000).toISOString(),
  },
  {
    id: 'demo-3',
    title: 'Deep Rest Protocol',
    focus: 'sleep',
    duration_minutes: 20,
    status: 'script_ready',
    audio_file_path: null,
    script: 'Let your body become heavy. [PAUSE 6s] There is nothing you need to solve right now. Nothing you need to remember. [PAUSE 4s] The day is complete. Your only task is to receive rest. [PAUSE 6s] Breathe out slowly. Let the weight of the day dissolve into the surface beneath you.',
    word_count: 54,
    created_at: new Date(Date.now() - 9 * 86400000).toISOString(),
  },
];

// ============ MY MEDITATIONS SUB-SECTION ============
function MyMeditationsSection({ user, refreshTrigger }) {
  const [meditations, setMeditations] = useState([]);
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  const [filter, setFilter] = useState({ status: '', focus: '' });
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isResynthesizing, setIsResynthesizing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showFullScript, setShowFullScript] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    if (user?.user_id) loadMeditations();
  }, [user, refreshTrigger, filter.status, filter.focus]);

  useEffect(() => {
    const audio = audioRef.current;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('ended', onEnded);
      audio.pause();
    };
  }, []);

  const loadMeditations = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.focus) params.append('focus', filter.focus);
      const response = await axios.get(`${API}/meditations/${user.user_id}?${params}`);
      setMeditations(response.data);
    } catch (error) {
      // Backend offline — show demo library
      setMeditations(DEMO_MEDITATIONS);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      title: 'Remove this meditation?',
      message: 'This cannot be undone.',
      confirmLabel: 'Remove',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/meditations/${id}`, { params: { user_id: user.user_id } });
          toast.success('Meditation removed');
          setMeditations(prev => prev.filter(m => m.id !== id));
          if (selectedMeditation?.id === id) selectMeditation(null);
        } catch (error) {
          toast.error('Could not remove meditation');
        }
      },
    });
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/meditations/${id}/duplicate`, null, { params: { user_id: user.user_id } });
      toast.success('Meditation duplicated');
      loadMeditations();
    } catch (error) {
      toast.error('Could not duplicate meditation');
    }
  };

  const handleResynthesize = async () => {
    if (!selectedMeditation) return;
    setIsResynthesizing(true);
    try {
      // Re-use the same voice_type that was originally used for this meditation
      const voiceTypeParam = selectedMeditation.voice_type || 'female_guide';
      await axios.post(`${API}/meditations/${selectedMeditation.id}/synthesize`, null, {
        params: { user_id: user.user_id, voice_type: voiceTypeParam }
      });
      toast.success('Audio re-generated');
      loadMeditations();
      setSelectedMeditation(prev => ({ ...prev, status: 'audio_ready' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not re-generate audio');
    } finally {
      setIsResynthesizing(false);
    }
  };

  const selectMeditation = (med) => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    setCurrentTime(0);
    // Pre-fill duration from stored metadata so player shows it before loadedmetadata
    setDuration(med?.audio_duration || 0);
    setShowFullScript(false);
    setSelectedMeditation(med);
  };

  const playAudio = () => {
    if (!selectedMeditation?.audio_file_path) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const src = `${API}/meditations/${selectedMeditation.id}/audio?user_id=${user.user_id}`;
      if (audioRef.current.src !== src) audioRef.current.src = src;
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.warn('Playback failed:', err);
          toast.error('Could not play audio — try again');
        });
    }
  };

  const handleSeek = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const focusLabels = {
    anxiety: 'Anxiety', sleep: 'Sleep', confidence: 'Confidence',
    grief: 'Grief', gratitude: 'Gratitude', stress: 'Stress',
    self_love: 'Self Love', morning: 'Morning', recovery: 'Recovery', custom: 'Custom'
  };

  const statusConfig = {
    draft:        { label: 'Draft',        color: '#F0A500' },
    script_ready: { label: 'Script Ready', color: '#2D8B87' },
    audio_ready:  { label: 'Ready',        color: '#1E6B68' }
  };

  const ctrl = { padding: '10px 14px', borderRadius: '10px', border: '1.5px solid rgba(30,107,104,0.25)', fontSize: '0.88rem', background: 'rgba(255,255,255,0.92)', color: '#333', outline: 'none' };

  let filtered = meditations.filter(m =>
    !searchQuery || m.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  if (sortBy === 'oldest') filtered = [...filtered].reverse();
  else if (sortBy === 'alpha') filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div>
      {confirmModal && <ConfirmModal {...confirmModal} onClose={() => setConfirmModal(null)} />}

      {/* Search + Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search meditations…"
          style={{ ...ctrl, flex: '1', minWidth: '160px' }}
        />
        <select value={filter.status} onChange={(e) => setFilter(p => ({ ...p, status: e.target.value }))} style={ctrl}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="script_ready">Script Ready</option>
          <option value="audio_ready">Ready</option>
        </select>
        <select value={filter.focus} onChange={(e) => setFilter(p => ({ ...p, focus: e.target.value }))} style={ctrl}>
          <option value="">All Focus</option>
          {Object.entries(focusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={ctrl}>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="alpha">A → Z</option>
        </select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#2D8B87', fontSize: '0.95rem' }}>
          Loading your meditations…
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filtered.length === 0 && (
        <div style={{ padding: '48px', textAlign: 'center', background: 'rgba(255,255,255,0.88)', borderRadius: '16px', color: '#999' }}>
          <div style={{ marginBottom: '12px', display:'flex', justifyContent:'center' }}><Headphones size={40} color="#B8D3CF"/></div>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            {meditations.length === 0 ? 'No meditations yet. Create your first one.' : 'No meditations match these filters.'}
          </p>
        </div>
      )}

      {/* Grid */}
      {!isLoading && filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px', marginBottom: selectedMeditation ? '24px' : '0' }}>
          {filtered.map((med) => {
            const sc = statusConfig[med.status] || { label: med.status, color: '#999' };
            const isSelected = selectedMeditation?.id === med.id;
            return (
              <div
                key={med.id}
                onClick={() => selectMeditation(isSelected ? null : med)}
                style={{
                  padding: '18px 20px',
                  background: isSelected ? 'rgba(30,107,104,0.07)' : 'rgba(255,255,255,0.92)',
                  borderRadius: '14px', cursor: 'pointer',
                  border: isSelected ? '2px solid #1E6B68' : '1.5px solid rgba(30,107,104,0.12)',
                  boxShadow: '0 2px 12px rgba(30,107,104,0.07)',
                  transition: 'all 0.18s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <h4 style={{ margin: 0, color: '#1E6B68', fontSize: '0.98rem', fontWeight: 600, lineHeight: 1.3 }}>{med.title}</h4>
                  <span style={{ padding: '3px 9px', background: sc.color, color: '#fff', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {sc.label}
                  </span>
                </div>
                <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 8px', background: 'rgba(30,107,104,0.1)', color: '#1E6B68', borderRadius: '20px', fontSize: '0.72rem' }}>
                    {focusLabels[med.focus] || med.focus}
                  </span>
                  <span style={{ padding: '2px 8px', background: 'rgba(184,211,207,0.4)', color: '#2D8B87', borderRadius: '20px', fontSize: '0.72rem' }}>
                    {med.length_minutes} min
                  </span>
                  {med.status === 'audio_ready' && (
                    <span style={{ padding: '2px 8px', background: 'rgba(30,107,104,0.08)', color: '#1E6B68', borderRadius: '20px', fontSize: '0.72rem' }}>
                      ♪ Audio
                    </span>
                  )}
                </div>
                <p style={{ margin: '8px 0 0', color: '#bbb', fontSize: '0.72rem' }}>
                  {new Date(med.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Panel */}
      {selectedMeditation && (
        <div style={{ padding: '28px', background: 'rgba(255,255,255,0.97)', borderRadius: '18px', boxShadow: '0 8px 40px rgba(30,107,104,0.12)', border: '1px solid rgba(30,107,104,0.1)' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div style={{ flex: 1, paddingRight: '16px' }}>
              <h3 style={{ margin: '0 0 10px', color: '#1E6B68', fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', lineHeight: 1.2 }}>
                {selectedMeditation.title}
              </h3>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {(() => {
                  const sc = statusConfig[selectedMeditation.status] || { label: selectedMeditation.status, color: '#999' };
                  return <span style={{ padding: '3px 10px', background: sc.color, color: '#fff', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600 }}>{sc.label}</span>;
                })()}
                <span style={{ padding: '3px 10px', background: 'rgba(30,107,104,0.1)', color: '#1E6B68', borderRadius: '20px', fontSize: '0.7rem' }}>
                  {focusLabels[selectedMeditation.focus] || selectedMeditation.focus}
                </span>
                <span style={{ padding: '3px 10px', background: 'rgba(184,211,207,0.35)', color: '#2D8B87', borderRadius: '20px', fontSize: '0.7rem' }}>
                  {selectedMeditation.length_minutes} min
                </span>
                {selectedMeditation.tone && (
                  <span style={{ padding: '3px 10px', background: 'rgba(184,211,207,0.2)', color: '#2D8B87', borderRadius: '20px', fontSize: '0.7rem' }}>
                    {selectedMeditation.tone}
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => selectMeditation(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#ccc', cursor: 'pointer', lineHeight: 1, padding: '2px' }}>×</button>
          </div>

          {/* Audio Player */}
          {selectedMeditation.status === 'audio_ready' && (
            <div style={{ padding: '20px', background: 'linear-gradient(135deg, rgba(30,107,104,0.06) 0%, rgba(184,211,207,0.15) 100%)', borderRadius: '14px', marginBottom: '20px', border: '1px solid rgba(30,107,104,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Play/Pause button */}
                <button
                  onClick={playAudio}
                  style={{
                    width: '52px', height: '52px', flexShrink: 0,
                    background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                    color: '#fff', border: 'none', borderRadius: '50%',
                    fontSize: '1.2rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(30,107,104,0.3)'
                  }}
                >
                  {isPlaying ? '⏸' : '▶'}
                </button>
                {/* Progress + time */}
                <div style={{ flex: 1 }}>
                  <div
                    onClick={handleSeek}
                    style={{ height: '6px', background: 'rgba(30,107,104,0.15)', borderRadius: '3px', cursor: 'pointer', marginBottom: '6px', position: 'relative' }}
                  >
                    <div style={{
                      height: '100%',
                      width: duration ? `${(currentTime / duration) * 100}%` : '0%',
                      background: 'linear-gradient(90deg, #1E6B68, #2D8B87)',
                      borderRadius: '3px',
                      transition: 'width 0.1s linear'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#999' }}>
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Script */}
          {selectedMeditation.script && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: '#1E6B68', fontSize: '0.9rem', fontWeight: 600, letterSpacing: '0.03em' }}>Script</h4>
                <button
                  onClick={() => setShowFullScript(s => !s)}
                  style={{ background: 'none', border: 'none', color: '#2D8B87', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {showFullScript ? 'Show less' : 'Read full script'}
                </button>
              </div>
              <div style={{
                padding: '16px',
                background: 'rgba(184,211,207,0.08)',
                borderRadius: '10px',
                maxHeight: showFullScript ? '400px' : '100px',
                overflow: 'auto',
                fontFamily: "'Georgia', serif",
                lineHeight: '1.8',
                color: '#666',
                fontSize: '0.88rem',
                transition: 'max-height 0.3s ease'
              }}>
                {selectedMeditation.script}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {selectedMeditation.script && (
              <button
                onClick={handleResynthesize}
                disabled={isResynthesizing}
                style={{ padding: '10px 18px', background: 'rgba(30,107,104,0.07)', color: '#1E6B68', border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: '20px', cursor: isResynthesizing ? 'not-allowed' : 'pointer', fontSize: '0.84rem', opacity: isResynthesizing ? 0.6 : 1 }}
              >
                {isResynthesizing ? 'Generating…' : '↺ Re-generate audio'}
              </button>
            )}
            <button
              onClick={() => handleDuplicate(selectedMeditation.id)}
              style={{ padding: '10px 18px', background: 'rgba(30,107,104,0.07)', color: '#1E6B68', border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: '20px', cursor: 'pointer', fontSize: '0.84rem' }}
            >
              ⎘ Duplicate
            </button>
            <button
              onClick={() => handleDelete(selectedMeditation.id)}
              style={{ padding: '10px 18px', background: 'rgba(192,57,43,0.05)', color: '#C0392B', border: '1px solid rgba(192,57,43,0.2)', borderRadius: '20px', cursor: 'pointer', fontSize: '0.84rem' }}
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ MAIN TRANSFORMATION SUITE CONTAINER ============
function TransformationSuite() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth0();
  // Land on Create: the one thing every plan can do right away (free users
  // have meditations included). Voice Lab (Pro) stays one tap away instead of
  // greeting new users with a paywall.
  const [activeTab, setActiveTab] = useState('create');
  const [user, setUser] = useState(null);
  const [hasVoice, setHasVoice] = useState(false);
  const [refreshMeditations, setRefreshMeditations] = useState(0);
  const [showMicrodosing, setShowMicrodosing] = useState(false);
  const [activeEnrollments, setActiveEnrollments] = useState([]);
  const [isTherapist, setIsTherapist] = useState(false);
  const [isMatrescence, setIsMatrescence] = useState(false);

  const subscription = useSubscription(user?.user_id);

  const checkVoiceStatus = async (userId) => {
    try {
      const response = await axios.get(`${API}/voice-profiles/${userId}/active`);
      setHasVoice(!!response.data);
    } catch (error) {
      // Also check user's legacy cloned voice
      try {
        const userResponse = await axios.get(`${API}/voice/status/${userId}`);
        setHasVoice(userResponse.data?.cloned_voice_status === 'ready');
      } catch (e) {
        setHasVoice(false);
      }
    }
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);

      // Role detection — therapists/doctors/institutions see a different Studio
      const storedRole = localStorage.getItem('hypseya_user_role');
      const userRole = parsedUser.role || storedRole || '';
      const clinicalRoles = ['therapist', 'doctor', 'institution'];
      if (clinicalRoles.includes(userRole) || clinicalRoles.includes(storedRole)) {
        setIsTherapist(true);
        return; // Skip patient-specific setup
      }

      // Matrescence mode detection
      const storedMode = localStorage.getItem('hypseya_user_mode');
      const userMode = parsedUser.mode || storedMode || 'standard';
      if (userMode === 'matrescence') {
        setIsMatrescence(true);
      }

      // Fallback: check hyp_facilitator key (set during FacilitatorOnboarding flow)
      try {
        const facilitatorData = localStorage.getItem('hyp_facilitator');
        if (facilitatorData) {
          const fac = JSON.parse(facilitatorData);
          if (fac && (fac.role || fac.id || fac._id)) {
            setIsTherapist(true);
            return;
          }
        }
      } catch (_) {}

      checkVoiceStatus(parsedUser.user_id);
      // Show microdosing tab for users with expanded states
      if (
        parsedUser.expanded_states === 'yes' ||
        parsedUser.microdosing_practice === 'psilocybin' ||
        parsedUser.microdosing_practice === 'expanded'
      ) {
        setShowMicrodosing(true);
      }
    }
  }, []);

  // Fetch active program enrollments for Programs hub
  useEffect(() => {
    const userId = authUser?.sub || user?.user_id || user?.email;
    if (!userId) return;
    axios.get(`${API}/programs/enrollment/${encodeURIComponent(userId)}`)
      .then(res => setActiveEnrollments(res.data.enrollments || []))
      .catch(() => {});
  }, [authUser, user]);

  // Short, single-word labels so the tab row stays legible even at 375px with
  // the full set of tabs (icons carry the rest of the meaning).
  const tabs = isMatrescence
    ? [
        { id: 'create', label: 'Create', icon: 'sparkles' },
        { id: 'voice_lab', label: 'Voice', icon: 'mic' },
        { id: 'library', label: 'Library', icon: 'healing' },
      ]
    : [
        { id: 'create', label: 'Create', icon: 'sparkles' },
        { id: 'voice_lab', label: 'Voice', icon: 'mic' },
        { id: 'library', label: 'Library', icon: 'healing' },
        ...(showMicrodosing ? [
          { id: 'microdosing', label: 'Micro', icon: 'growth' },
          { id: 'integration', label: 'Integrate', icon: 'integration' },
        ] : []),
      ];

  // ── Therapist / Clinical role — show dedicated clinical workspace ───────────
  if (isTherapist) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
        padding: '40px 20px 100px',
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#2D8B87', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Clinical Workspace
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', color: '#1E3A39', margin: '0 0 10px', fontWeight: '500' }}>
              Therapist Studio
            </h1>
            <p style={{ color: '#8FA3A0', fontSize: '0.9rem', margin: 0, lineHeight: 1.6 }}>
              Your clinical tools — patients, programs, reports and alerts.
            </p>
          </div>

          {/* Primary action — Dashboard */}
          <button
            onClick={() => navigate('/therapist-dashboard')}
            style={{
              width: '100%', padding: '20px 24px', marginBottom: '12px',
              background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              border: 'none', borderRadius: '20px', cursor: 'pointer',
              textAlign: 'left', display: 'flex', alignItems: 'center', gap: '16px',
              boxShadow: '0 4px 20px rgba(30,107,104,0.2)',
            }}
          >
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="7" cy="6" r="3" stroke="white" strokeWidth="1.4"/>
                <circle cx="13" cy="6" r="3" stroke="white" strokeWidth="1.4"/>
                <path d="M1 17c0-3 2.5-5 6-5" stroke="white" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M13 12c3.5 0 6 2 6 5" stroke="white" strokeWidth="1.4" strokeLinecap="round"/>
                <circle cx="13" cy="14" r="3" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.2"/>
                <path d="M11.5 14l1 1 2-2" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#fff', marginBottom: '2px' }}>Patient Dashboard</div>
              <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.72)' }}>Patients · ERI · PHQ-9/GAD-7 · Risk alerts · Clinical notes</div>
            </div>
            <svg style={{ marginLeft: 'auto', flexShrink: 0 }} width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 8h8M9 5l3 3-3 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Secondary actions grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            {[
              {
                label: 'Clinical Reports',
                sub: 'PDF export · Progress',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <rect x="4" y="2" width="12" height="16" rx="2" stroke="#1E6B68" strokeWidth="1.4"/>
                    <path d="M7 7h6M7 10h6M7 13h4" stroke="#1E6B68" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                ),
                path: '/clinical-report',
              },
              {
                label: 'Programs',
                sub: 'Assign · Track progress',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <rect x="2" y="3" width="16" height="14" rx="2" stroke="#1E6B68" strokeWidth="1.4"/>
                    <path d="M2 7h16" stroke="#1E6B68" strokeWidth="1.3"/>
                    <path d="M7 11l2 2 4-4" stroke="#1E6B68" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ),
                path: '/programs',
              },
              {
                label: 'Scientific Foundation',
                sub: 'Evidence · References',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="8" stroke="#1E6B68" strokeWidth="1.4"/>
                    <path d="M10 6v5l3 2" stroke="#1E6B68" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                ),
                path: '/scientific-foundation',
              },
              {
                label: 'ERI Methodology',
                sub: 'White paper · Validation',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M3 15 L7 9 L11 12 L15 5" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="15" cy="5" r="2" stroke="#1E6B68" strokeWidth="1.2"/>
                  </svg>
                ),
                path: '/eri-methodology',
              },
            ].map(item => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                style={{
                  padding: '16px', background: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(30,107,104,0.12)', borderRadius: '16px',
                  cursor: 'pointer', textAlign: 'left',
                  boxShadow: '0 2px 12px rgba(30,107,104,0.05)',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ marginBottom: '8px' }}>{item.icon}</div>
                <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1E3A39', marginBottom: '2px' }}>{item.label}</div>
                <div style={{ fontSize: '0.7rem', color: '#AAB8B6' }}>{item.sub}</div>
              </button>
            ))}
          </div>

          {/* Compliance */}
          <button
            onClick={() => navigate('/clinical-compliance')}
            style={{
              width: '100%', padding: '14px 18px',
              background: 'rgba(30,107,104,0.04)', border: '1px solid rgba(30,107,104,0.1)',
              borderRadius: '14px', cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: '12px',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L3 5v6c0 4 3 7 7 8 4-1 7-4 7-8V5L10 2z" stroke="#8FA3A0" strokeWidth="1.3"/>
              <path d="M7 10l2 2 4-4" stroke="#8FA3A0" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6B8B87' }}>Clinical Compliance</div>
              <div style={{ fontSize: '0.7rem', color: '#AAB8B6' }}>GDPR Art. 9 · DPA · Data governance</div>
            </div>
            <svg style={{ marginLeft: 'auto', flexShrink: 0 }} width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 6h6M6.5 3.5L9 6l-2.5 2.5" stroke="#AAB8B6" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Notice */}
          <div style={{ marginTop: '20px', padding: '14px 18px', background: 'rgba(184,144,106,0.07)', border: '1px solid rgba(184,144,106,0.2)', borderRadius: '14px' }}>
            <p style={{ margin: 0, fontSize: '0.76rem', color: '#8B6B50', lineHeight: 1.6 }}>
              <strong>Clinical account</strong> — Voice Lab and meditation creation are patient tools and are not available in this workspace. If you need to access them, switch to a patient account.
            </p>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F7F3EC 0%, #EFE7DD 50%, #B8D3CF 100%)',
      padding: '40px 20px 100px'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '2.5rem',
            color: '#1E6B68',
            marginBottom: '10px'
          }}>
            Studio
          </h1>

          {/* Subscription badge */}
          {!subscription.loading && (
            <div style={{ marginBottom: '10px' }}>
              {subscription.isPro ? (
                <button
                  onClick={subscription.openPortal}
                  style={{ padding: '5px 14px', background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)', color: '#fff', border: 'none', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.08em', cursor: 'pointer' }}
                >
                  ✦ HYPSEYA PRO · Manage
                </button>
              ) : (
                <span style={{ padding: '5px 14px', background: 'rgba(30,107,104,0.08)', color: '#1E6B68', borderRadius: '20px', fontSize: '0.76rem', fontWeight: '600' }}>
                  Free plan · {subscription.meditationsLeft ?? 0} meditation{subscription.meditationsLeft !== 1 ? 's' : ''} left
                </span>
              )}
            </div>
          )}

          <p style={{
            color: '#2D8B87',
            fontSize: '1.1rem',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Create personalized meditations in your own voice
          </p>
        </div>

        {/* ── Programs Hub ───────────────────────────────────────────────── */}
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '32px',
          background: 'rgba(255, 255, 255, 0.8)',
          padding: '8px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(30, 107, 104, 0.1)'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '10px 4px',
                background: activeTab === tab.id
                  ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)'
                  : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#1E6B68',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                transition: 'all 0.2s ease',
                minWidth: 0
              }}
            >
              <IkiSymbol name={tab.icon} size={18} />
              <span style={{ fontSize: '0.72rem', lineHeight: 1.2, textAlign: 'center' }}>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* No-voice notice — informational, not alarming: guide voices work
            without a cloned voice, so this is an invitation rather than a block. */}
        {activeTab === 'create' && !hasVoice && (
          <div style={{
            padding: '10px 16px',
            background: 'rgba(184,211,207,0.14)',
            borderRadius: '12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            border: '1px solid rgba(184,211,207,0.5)'
          }}>
            <Mic size={16} color="#2D8B87"/>
            <p style={{ margin: 0, color: '#4A6B68', fontSize: '0.82rem', flex: 1 }}>
              You'll narrate with a guide voice — record yours in Voice Lab to hear meditations in your own voice.
            </p>
            <button
              onClick={() => setActiveTab('voice_lab')}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                color: '#1E6B68',
                border: '1.5px solid rgba(30,107,104,0.3)',
                borderRadius: '14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontSize: '0.78rem',
                fontWeight: '600'
              }}
            >
              Voice Lab →
            </button>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'voice_lab' && (
          <VoiceLabSection
            user={user}
            onNavigate={setActiveTab}
            subscription={subscription}
            onVoiceCreated={() => {
              setHasVoice(true);
            }}
          />
        )}

        {activeTab === 'create' && (
          <CreateMeditationSection
            user={user}
            hasVoice={hasVoice}
            onNavigate={setActiveTab}
            subscription={subscription}
            onMeditationCreated={() => {
              setRefreshMeditations(prev => prev + 1);
              subscription.refresh();
            }}
          />
        )}

        {activeTab === 'library' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
              <button
                onClick={() => navigate('/my-meditations')}
                style={{
                  background: 'none', border: 'none',
                  color: '#2D8B87', fontSize: '0.82rem',
                  cursor: 'pointer', padding: '4px 0',
                  textDecoration: 'underline', textUnderlineOffset: '3px',
                }}
              >
                Full library →
              </button>
            </div>
            <MyMeditationsSection
              user={user}
              refreshTrigger={refreshMeditations}
            />
          </>
        )}

        {activeTab === 'microdosing' && (
          <div style={{ margin: '0 -20px' }}>
            <MicroDosingCalendar embedded />
          </div>
        )}

        {activeTab === 'integration' && (
          <IntegrationStudioSection user={user} />
        )}
      </div>
    </div>
  );
}

export default TransformationSuite;
